import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  Portal,
  Select,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  useColorModeValue,
  usePrefersReducedMotion,
  useToast,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import jsPDF from 'jspdf';
import { FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight, FaClipboard, FaEye, FaEyeSlash, FaMagic, FaTasks, FaTimes, FaDownload, FaHistory, FaSearch, FaExchangeAlt } from 'react-icons/fa';
import {
  LegalDraftArtifact,
  LegalDraftSessionState,
  fieldsFromPlaceholderValues,
  removeChatSocReferences,
  placeholderValuesFromFields,
} from '../helpers/legalArtifacts';
import fetchArtifactEdit, { type ArtifactEditResponse } from '../helpers/fetchArtifactEdit';
import { isLegalCodeUrl, isSullivanOnCompUrl } from '../helpers/urlSafety';

interface LegalDraftPanelProps {
  artifact: LegalDraftArtifact;
  apiAddress: string;
  chatId: string;
  onClose: () => void;
  onChatResponse?: (question: string, answer: string) => void;
  onUpdateArtifact?: (newText: string) => void;
  persistedState?: LegalDraftSessionState;
  persistedStateLoading?: boolean;
  onPersistState?: (state: LegalDraftSessionState) => void;
  chatContext?: string;
  showChatSocReferences?: boolean;
  onShowChatSocReferencesChange?: (showChatSocReferences: boolean) => void;
}

interface DraftSelection {
  selectedText: string;
  beforeContext: string;
  afterContext: string;
  sourceSelectedText?: string;
  sourceBeforeContext?: string;
  sourceAfterContext?: string;
  rects: SelectionRect[];
  x: number;
  y: number;
  viewportTop: number;
  viewportBottom: number;
}

interface SelectionRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function getRangeSelectionRects(range: Range, root: HTMLElement): SelectionRect[] {
  const rootRect = root.getBoundingClientRect();
  const toSelectionRect = (rect: DOMRect): SelectionRect => ({
    top: rect.top - rootRect.top + root.scrollTop,
    left: rect.left - rootRect.left + root.scrollLeft,
    width: rect.width,
    height: rect.height,
  });
  const isUsableRect = (rect: DOMRect | SelectionRect) => rect.width > 1 && rect.height > 1;

  const textRects: SelectionRect[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text;
    const text = textNode.textContent || '';

    if (!text.trim() || !range.intersectsNode(textNode)) continue;

    let startOffset = textNode === range.startContainer ? range.startOffset : 0;
    let endOffset = textNode === range.endContainer ? range.endOffset : text.length;

    while (startOffset < endOffset && /\s/.test(text[startOffset])) startOffset += 1;
    while (endOffset > startOffset && /\s/.test(text[endOffset - 1])) endOffset -= 1;
    if (startOffset >= endOffset) continue;

    const textRange = document.createRange();
    textRange.setStart(textNode, startOffset);
    textRange.setEnd(textNode, endOffset);
    Array.from(textRange.getClientRects())
      .filter(isUsableRect)
      .forEach(clientRect => textRects.push(toSelectionRect(clientRect)));
    textRange.detach();
  }

  if (textRects.length > 0) return textRects;

  return Array.from(range.getClientRects())
    .filter(isUsableRect)
    .map(toSelectionRect);
}

const panelIn = keyframes`
  from { opacity: 0; transform: translateX(14px) scale(0.992); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;

const selectionEditPulse = keyframes`
  0% { opacity: 0.24; transform: scaleX(1); }
  50% { opacity: 0.46; transform: scaleX(1.012); }
  100% { opacity: 0.24; transform: scaleX(1); }
`;

function getSelectionPopoverLayout(selection: DraftSelection, isNoteOpen: boolean) {
  const viewportHeight = window.innerHeight;
  const margin = 12;
  const openPopoverHeight = 236;
  const minimumOpenHeight = 172;

  if (!isNoteOpen) {
    return {
      top: Math.min(Math.max(selection.viewportBottom + 10, 72), viewportHeight - 56),
      transform: 'translate(-50%, 0)',
      transformOrigin: 'top center',
      maxH: undefined,
    };
  }

  const spaceBelow = viewportHeight - selection.viewportBottom - margin;
  const spaceAbove = selection.viewportTop - margin;
  const canFitBelow = spaceBelow >= minimumOpenHeight;
  const canFitAbove = spaceAbove >= minimumOpenHeight;

  if (!canFitBelow && canFitAbove) {
    const top = Math.max(margin, selection.viewportTop - 10);
    return {
      top,
      transform: 'translate(-50%, -100%)',
      transformOrigin: 'bottom center',
      maxH: `${Math.max(minimumOpenHeight, Math.min(openPopoverHeight, spaceAbove - 12))}px`,
    };
  }

  const preferredTop = selection.viewportBottom + 10;
  const latestTop = viewportHeight - Math.min(openPopoverHeight, viewportHeight - margin * 2) - margin;
  const top = Math.min(Math.max(preferredTop, margin), Math.max(margin, latestTop));

  return {
    top,
    transform: 'translate(-50%, 0)',
    transformOrigin: 'top center',
    maxH: `${Math.max(120, viewportHeight - top - margin)}px`,
  };
}

function getPlaceholders(text: string): string[] {
  const seen = new Set<string>();
  return Array.from(text.matchAll(/\[[^\]\n]{2,90}\](?!\()/g))
    .map(match => match[0])
    .filter(placeholder => {
      const content = placeholder.slice(1, -1).trim();
      if (!content) return false;
      if (/^(SOC|LC|Labor Code|Section)\b/i.test(content)) return false;
      if (/^AI-Generated Document:/i.test(content)) return false;
      if (/^https?:\/\//i.test(content)) return false;
      if (seen.has(placeholder)) return false;
      seen.add(placeholder);
      return true;
    });
}

function prepareDraftPreview(text: string): string {
  return text
    .replace(/\\n/g, '\n')
    .replace(/<<case[^>]*>>([\s\S]*?)<<\/case>>/g, '$1')
    .replace(/<</g, '')
    .replace(/>>/g, '');
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getPlaceholderLabel(placeholder: string): string {
  return placeholder
    .slice(1, -1)
    .replace(/^(INSERT|ADD|REVIEW|CONFIRM):?\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function applyPlaceholderReplacements(
  text: string,
  replacements: Record<string, string>,
  highlightedPlaceholder?: string | null,
  highlightUnfilled = false,
): string {
  const placeholdersToReplace = new Set(Object.keys(replacements));
  if (highlightedPlaceholder) placeholdersToReplace.add(highlightedPlaceholder);
  if (highlightUnfilled) getPlaceholders(text).forEach(placeholder => placeholdersToReplace.add(placeholder));

  return Array.from(placeholdersToReplace).reduce((draftText, placeholder) => {
    const trimmed = (replacements[placeholder] || '').trim();
    const shouldHighlight = placeholder === highlightedPlaceholder;
    const shouldMarkUnfilled = highlightUnfilled && !trimmed;
    if (!trimmed && !shouldHighlight && !shouldMarkUnfilled) return draftText;
    const fieldLabel = getPlaceholderLabel(placeholder);

    const replacement = shouldHighlight
      ? `<mark data-legal-draft-field-highlight="true" data-placeholder="${escapeHtml(placeholder)}">${escapeHtml(trimmed || fieldLabel)}</mark>`
      : shouldMarkUnfilled
        ? `<mark data-legal-draft-missing-field="true" data-placeholder="${escapeHtml(placeholder)}">${escapeHtml(fieldLabel)}</mark>`
        : trimmed;

    return draftText.replace(new RegExp(escapeRegExp(placeholder), 'g'), replacement);
  }, text);
}

function stripMarkdownForPdf(text: string): string {
  return prepareDraftPreview(text)
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .split('\n')
    .map(line => stripAngleBracketMarkup(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function stripAngleBracketMarkup(text: string, replacement = ''): string {
  const output: string[] = [];
  let index = 0;

  while (index < text.length) {
    if (text[index] === '<') {
      const tagEnd = text.indexOf('>', index + 1);
      if (tagEnd !== -1) {
        if (replacement) output.push(replacement);
        index = tagEnd + 1;
        continue;
      }
      index += 1;
      continue;
    }
    if (text[index] === '>') {
      index += 1;
      continue;
    }
    output.push(text[index]);
    index += 1;
  }

  return output.join('');
}

function getPdfFileName(title: string): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${slug || 'draft'}.pdf`;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function collapseWhitespace(value: string): string {
  return value
    .replace(/[•◦▪‣⁃]\s*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizePatchSearchChar(char: string): string {
  return char
    .normalize('NFKC')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2010-\u2015]/g, '-')
    .replace(/\u00A0/g, ' ')
    .toLowerCase();
}

function getFlexibleTextRange(text: string, selectedText: string): { start: number; end: number } | null {
  const normalizedNeedle = Array.from(collapseWhitespace(selectedText))
    .map(normalizePatchSearchChar)
    .join('');
  if (!normalizedNeedle) return null;

  let normalizedText = '';
  const normalizedToOriginal: number[] = [];
  let previousWasWhitespace = false;

  for (let index = 0; index < text.length;) {
    const char = Array.from(text.slice(index))[0];
    const sourceIndex = index;
    index += char.length;

    const normalizedChar = normalizePatchSearchChar(char);
    if (/\s/.test(normalizedChar)) {
      if (!previousWasWhitespace) {
        normalizedText += ' ';
        normalizedToOriginal.push(sourceIndex);
      }
      previousWasWhitespace = true;
      continue;
    }

    const outputChars = Array.from(normalizedChar);
    for (let outputIndex = 0; outputIndex < outputChars.length; outputIndex += 1) {
      const outputChar = outputChars[outputIndex];
      normalizedText += outputChar;
      normalizedToOriginal.push(sourceIndex);
    }
    previousWasWhitespace = false;
  }

  const normalizedIndex = normalizedText.indexOf(normalizedNeedle);
  if (normalizedIndex === -1) return null;

  const start = normalizedToOriginal[normalizedIndex];
  const end = normalizedToOriginal[normalizedIndex + normalizedNeedle.length - 1] + 1;
  return { start, end };
}

function tokenizeForReviewDiff(text: string): string[] {
  return text.match(/\s+|[^\s]+/g) || [];
}

function buildFocusedDiffMarkdown(previousText: string, nextText: string): string | null {
  if (previousText === nextText) return null;

  let prefixLength = 0;
  const shortestLength = Math.min(previousText.length, nextText.length);
  while (
    prefixLength < shortestLength
    && previousText[prefixLength] === nextText[prefixLength]
  ) {
    prefixLength += 1;
  }

  let suffixLength = 0;
  while (
    suffixLength < previousText.length - prefixLength
    && suffixLength < nextText.length - prefixLength
    && previousText[previousText.length - 1 - suffixLength] === nextText[nextText.length - 1 - suffixLength]
  ) {
    suffixLength += 1;
  }

  const unchangedStart = previousText.slice(0, prefixLength);
  const removed = previousText.slice(prefixLength, previousText.length - suffixLength);
  const added = nextText.slice(prefixLength, nextText.length - suffixLength);
  const unchangedEnd = previousText.slice(previousText.length - suffixLength);

  return [
    escapeHtml(unchangedStart),
    removed ? `<del data-legal-draft-change="remove">${escapeHtml(removed)}</del>` : '',
    added ? `<ins data-legal-draft-change="add">${escapeHtml(added)}</ins>` : '',
    escapeHtml(unchangedEnd),
  ].join('');
}

function buildReviewDiffMarkdown(previousText: string, nextText: string): string | null {
  if (previousText === nextText) return null;

  const previousTokens = tokenizeForReviewDiff(previousText);
  const nextTokens = tokenizeForReviewDiff(nextText);
  if (!previousTokens.length || !nextTokens.length) {
    return buildFocusedDiffMarkdown(previousText, nextText);
  }
  if (previousTokens.length * nextTokens.length > 180000) {
    return buildFocusedDiffMarkdown(previousText, nextText);
  }

  const rows = previousTokens.length + 1;
  const cols = nextTokens.length + 1;
  const table = Array.from({ length: rows }, () => new Uint16Array(cols));

  for (let previousIndex = previousTokens.length - 1; previousIndex >= 0; previousIndex -= 1) {
    for (let nextIndex = nextTokens.length - 1; nextIndex >= 0; nextIndex -= 1) {
      table[previousIndex][nextIndex] = previousTokens[previousIndex] === nextTokens[nextIndex]
        ? table[previousIndex + 1][nextIndex + 1] + 1
        : Math.max(table[previousIndex + 1][nextIndex], table[previousIndex][nextIndex + 1]);
    }
  }

  const parts: Array<{ type: 'same' | 'add' | 'remove'; value: string }> = [];
  let previousIndex = 0;
  let nextIndex = 0;

  const pushPart = (type: 'same' | 'add' | 'remove', value: string) => {
    const previousPart = parts[parts.length - 1];
    if (previousPart?.type === type) {
      previousPart.value += value;
      return;
    }
    parts.push({ type, value });
  };

  while (previousIndex < previousTokens.length && nextIndex < nextTokens.length) {
    if (previousTokens[previousIndex] === nextTokens[nextIndex]) {
      pushPart('same', previousTokens[previousIndex]);
      previousIndex += 1;
      nextIndex += 1;
    } else if (table[previousIndex + 1][nextIndex] >= table[previousIndex][nextIndex + 1]) {
      pushPart('remove', previousTokens[previousIndex]);
      previousIndex += 1;
    } else {
      pushPart('add', nextTokens[nextIndex]);
      nextIndex += 1;
    }
  }

  while (previousIndex < previousTokens.length) {
    pushPart('remove', previousTokens[previousIndex]);
    previousIndex += 1;
  }
  while (nextIndex < nextTokens.length) {
    pushPart('add', nextTokens[nextIndex]);
    nextIndex += 1;
  }

  return parts
    .map(part => {
      const value = escapeHtml(part.value);
      if (part.type === 'add') return `<ins data-legal-draft-change="add">${value}</ins>`;
      if (part.type === 'remove') return `<del data-legal-draft-change="remove">${value}</del>`;
      return value;
    })
    .join('');
}

function buildLocalizedReviewDiffMarkdown(previousText: string, nextText: string): string | null {
  if (previousText === nextText) return null;

  let prefixLength = 0;
  const shortestLength = Math.min(previousText.length, nextText.length);
  while (
    prefixLength < shortestLength
    && previousText[prefixLength] === nextText[prefixLength]
  ) {
    prefixLength += 1;
  }

  let suffixLength = 0;
  while (
    suffixLength < previousText.length - prefixLength
    && suffixLength < nextText.length - prefixLength
    && previousText[previousText.length - 1 - suffixLength] === nextText[nextText.length - 1 - suffixLength]
  ) {
    suffixLength += 1;
  }

  const unchangedStart = previousText.slice(0, prefixLength);
  const changedPrevious = previousText.slice(prefixLength, previousText.length - suffixLength);
  const changedNext = nextText.slice(prefixLength, nextText.length - suffixLength);
  const unchangedEnd = previousText.slice(previousText.length - suffixLength);
  const localizedDiff = buildReviewDiffMarkdown(changedPrevious, changedNext)
    || buildFocusedDiffMarkdown(changedPrevious, changedNext)
    || [
      changedPrevious ? `<del data-legal-draft-change="remove">${escapeHtml(changedPrevious)}</del>` : '',
      changedNext ? `<ins data-legal-draft-change="add">${escapeHtml(changedNext)}</ins>` : '',
    ].join('');

  return [
    escapeHtml(unchangedStart),
    localizedDiff,
    escapeHtml(unchangedEnd),
  ].join('');
}

function getTextRange(text: string, selectedText: string): { start: number; end: number } | null {
  const exactIndex = text.indexOf(selectedText);
  if (exactIndex !== -1) {
    return { start: exactIndex, end: exactIndex + selectedText.length };
  }

  const normalizedNeedle = collapseWhitespace(selectedText);
  if (!normalizedNeedle) return null;

  let normalizedText = '';
  const normalizedToOriginal: number[] = [];
  let previousWasWhitespace = false;

  Array.from(text).forEach((char, index) => {
    if (/\s/.test(char)) {
      if (!previousWasWhitespace) {
        normalizedText += ' ';
        normalizedToOriginal.push(index);
      }
      previousWasWhitespace = true;
      return;
    }

    normalizedText += char;
    normalizedToOriginal.push(index);
    previousWasWhitespace = false;
  });

  const normalizedIndex = normalizedText.indexOf(normalizedNeedle);
  if (normalizedIndex === -1) return null;

  const start = normalizedToOriginal[normalizedIndex];
  const end = normalizedToOriginal[normalizedIndex + normalizedNeedle.length - 1] + 1;
  return { start, end };
}

function findClosingParen(text: string, startIndex: number): number {
  let depth = 0;

  for (let index = startIndex; index < text.length; index += 1) {
    const char = text[index];
    if (char === '(') depth += 1;
    if (char === ')') {
      depth -= 1;
      if (depth === 0) return index;
    }
  }

  return -1;
}

function getRenderedMarkdownChars(text: string): Array<{ char: string; sourceIndex: number }> {
  const chars: Array<{ char: string; sourceIndex: number }> = [];
  let index = 0;
  let isLineStart = true;

  while (index < text.length) {
    const caseTag = text.slice(index).match(/^<<\/?case[^>]*>>/);
    if (caseTag?.[0]) {
      index += caseTag[0].length;
      continue;
    }

    if (isLineStart) {
      const linePrefix = text.slice(index).match(/^(?:[ \t]{0,3}(?:>\s*)?)*(?:#{1,6}\s+|[-+*]\s+|\d{1,3}[.)]\s+|[a-zA-Z][.)]\s+|\[[ xX]\]\s+)/);
      if (linePrefix?.[0]) {
        index += linePrefix[0].length;
        isLineStart = false;
        continue;
      }
    }

    if (text[index] === '[') {
      const labelEnd = text.indexOf(']', index + 1);
      if (labelEnd !== -1 && text[labelEnd + 1] === '(') {
        const linkEnd = findClosingParen(text, labelEnd + 1);
        if (linkEnd !== -1) {
          for (let labelIndex = index + 1; labelIndex < labelEnd; labelIndex += 1) {
            chars.push({ char: text[labelIndex], sourceIndex: labelIndex });
          }
          isLineStart = false;
          index = linkEnd + 1;
          continue;
        }
      }
    }

    if ((text.startsWith('**', index) || text.startsWith('__', index)) && text.indexOf(text.slice(index, index + 2), index + 2) !== -1) {
      index += 2;
      continue;
    }

    if ((text[index] === '*' || text[index] === '_' || text[index] === '`') && text.indexOf(text[index], index + 1) !== -1) {
      index += 1;
      continue;
    }

    chars.push({ char: text[index], sourceIndex: index });
    isLineStart = text[index] === '\n' || text[index] === '\r';
    index += 1;
  }

  return chars;
}

function getRenderedDraftSourceChars(
  text: string,
  replacements: Record<string, string>,
): Array<{ char: string; sourceStart: number; sourceEnd: number }> {
  const chars: Array<{ char: string; sourceStart: number; sourceEnd: number }> = [];
  const placeholders = getPlaceholders(text);
  let index = 0;
  let isLineStart = true;

  const pushMappedText = (value: string, sourceStart: number, sourceEnd: number) => {
    Array.from(value).forEach(char => {
      chars.push({ char, sourceStart, sourceEnd });
    });
  };

  while (index < text.length) {
    const caseTag = text.slice(index).match(/^<<\/?case[^>]*>>/);
    if (caseTag?.[0]) {
      index += caseTag[0].length;
      continue;
    }

    if (isLineStart) {
      const linePrefix = text.slice(index).match(/^(?:[ \t]{0,3}(?:>\s*)?)*(?:#{1,6}\s+|[-+*]\s+|\d{1,3}[.)]\s+|[a-zA-Z][.)]\s+|\[[ xX]\]\s+)/);
      if (linePrefix?.[0]) {
        index += linePrefix[0].length;
        isLineStart = false;
        continue;
      }
    }

    let placeholder = '';
    for (let placeholderIndex = 0; placeholderIndex < placeholders.length; placeholderIndex += 1) {
      const candidate = placeholders[placeholderIndex];
      if (text.startsWith(candidate, index)) {
        placeholder = candidate;
        break;
      }
    }
    if (placeholder) {
      const visiblePlaceholderText = (replacements[placeholder] || '').trim() || getPlaceholderLabel(placeholder);
      pushMappedText(visiblePlaceholderText, index, index + placeholder.length);
      index += placeholder.length;
      isLineStart = false;
      continue;
    }

    if (text[index] === '[') {
      const labelEnd = text.indexOf(']', index + 1);
      if (labelEnd !== -1 && text[labelEnd + 1] === '(') {
        const linkEnd = findClosingParen(text, labelEnd + 1);
        if (linkEnd !== -1) {
          for (let labelIndex = index + 1; labelIndex < labelEnd; labelIndex += 1) {
            chars.push({ char: text[labelIndex], sourceStart: labelIndex, sourceEnd: labelIndex + 1 });
          }
          isLineStart = false;
          index = linkEnd + 1;
          continue;
        }
      }
    }

    if ((text.startsWith('**', index) || text.startsWith('__', index)) && text.indexOf(text.slice(index, index + 2), index + 2) !== -1) {
      index += 2;
      continue;
    }

    if ((text[index] === '*' || text[index] === '_' || text[index] === '`') && text.indexOf(text[index], index + 1) !== -1) {
      index += 1;
      continue;
    }

    const char = Array.from(text.slice(index))[0];
    chars.push({ char, sourceStart: index, sourceEnd: index + char.length });
    isLineStart = char === '\n' || char === '\r';
    index += char.length;
  }

  return chars;
}

function markdownLinkRanges(text: string): Array<{ start: number; labelStart: number; labelEnd: number; end: number }> {
  const ranges: Array<{ start: number; labelStart: number; labelEnd: number; end: number }> = [];
  let index = 0;

  while (index < text.length) {
    if (text[index] !== '[') {
      index += 1;
      continue;
    }

    const labelEnd = text.indexOf(']', index + 1);
    if (labelEnd === -1 || text[labelEnd + 1] !== '(') {
      index += 1;
      continue;
    }

    const linkEnd = findClosingParen(text, labelEnd + 1);
    if (linkEnd === -1) {
      index += 1;
      continue;
    }

    ranges.push({
      start: index,
      labelStart: index + 1,
      labelEnd,
      end: text[linkEnd + 1] === ']' ? linkEnd + 2 : linkEnd + 1,
    });
    index = linkEnd + 1;
  }

  return ranges;
}

function expandRangeToRawMarkdownSyntax(text: string, range: { start: number; end: number }): { start: number; end: number } {
  let nextRange = { ...range };
  let didExpand = true;

  while (didExpand) {
    didExpand = false;

    const links = markdownLinkRanges(text);
    for (let linkIndex = 0; linkIndex < links.length; linkIndex += 1) {
      const linkRange = links[linkIndex];
      const overlapsVisibleLabel = linkRange.labelStart < nextRange.end && linkRange.labelEnd > nextRange.start;
      const overlapsLinkSyntax = linkRange.start < nextRange.end && linkRange.end > nextRange.start;
      if (overlapsVisibleLabel || overlapsLinkSyntax) {
        const start = Math.min(nextRange.start, linkRange.start);
        const end = Math.max(nextRange.end, linkRange.end);
        if (start !== nextRange.start || end !== nextRange.end) {
          nextRange = { start, end };
          didExpand = true;
        }
      }
    }

    const caseMatches = Array.from(text.matchAll(/<<case[^>]*>>/g));
    for (let caseIndex = 0; caseIndex < caseMatches.length; caseIndex += 1) {
      const openMatch = caseMatches[caseIndex];
      const openStart = openMatch.index ?? -1;
      if (openStart < 0) continue;
      const openEnd = openStart + openMatch[0].length;
      const closeStart = text.indexOf('<</case>>', openEnd);
      if (closeStart === -1) continue;
      const closeEnd = closeStart + '<</case>>'.length;
      const overlapsCase = openStart < nextRange.end && closeEnd > nextRange.start;
      const selectedCaseContent = openEnd < nextRange.end && closeStart > nextRange.start;
      if (overlapsCase && selectedCaseContent) {
        const start = Math.min(nextRange.start, openStart);
        const end = Math.max(nextRange.end, closeEnd);
        if (start !== nextRange.start || end !== nextRange.end) {
          nextRange = { start, end };
          didExpand = true;
        }
      }
    }

    const pairedMarkers = [
      ['**', '**'],
      ['__', '__'],
      ['`', '`'],
      ['*', '*'],
      ['_', '_'],
    ];
    for (let markerIndex = 0; markerIndex < pairedMarkers.length; markerIndex += 1) {
      const [openMarker, closeMarker] = pairedMarkers[markerIndex];
      if (
        nextRange.start >= openMarker.length
        && text.slice(nextRange.start - openMarker.length, nextRange.start) === openMarker
        && text.slice(nextRange.end, nextRange.end + closeMarker.length) === closeMarker
      ) {
        nextRange = {
          start: nextRange.start - openMarker.length,
          end: nextRange.end + closeMarker.length,
        };
        didExpand = true;
      }
    }
  }

  return nextRange;
}

function getMarkdownRawSelectionRange(text: string, selectedText: string): { start: number; end: number } | null {
  const renderedChars = getRenderedMarkdownChars(text);
  const renderedText = renderedChars.map(({ char }) => char).join('');
  const range = getTextRange(renderedText, selectedText);
  if (!range) return null;

  const selectedChars = renderedChars.slice(range.start, range.end)
    .filter(({ char }) => char !== '\n' && char !== '\r');
  if (!selectedChars.length) return null;

  const rawRange = {
    start: Math.min(...selectedChars.map(({ sourceIndex }) => sourceIndex)),
    end: Math.max(...selectedChars.map(({ sourceIndex }) => sourceIndex + 1)),
  };

  return expandRangeToMarkdownListPrefix(text, expandRangeToRawMarkdownSyntax(text, rawRange));
}

function getDraftSourceSelectionRange(
  text: string,
  selectedText: string,
  replacements: Record<string, string>,
): { start: number; end: number } | null {
  const renderedChars = getRenderedDraftSourceChars(text, replacements);
  const renderedText = renderedChars.map(({ char }) => char).join('');
  const range = getTextRange(renderedText, selectedText);
  if (!range) return null;

  const selectedChars = renderedChars.slice(range.start, range.end)
    .filter(({ char }) => char !== '\n' && char !== '\r');
  if (!selectedChars.length) return null;

  const rawRange = {
    start: Math.min(...selectedChars.map(({ sourceStart }) => sourceStart)),
    end: Math.max(...selectedChars.map(({ sourceEnd }) => sourceEnd)),
  };

  return expandRangeToMarkdownListPrefix(text, expandRangeToRawMarkdownSyntax(text, rawRange));
}

function expandRangeToMarkdownListPrefix(text: string, range: { start: number; end: number }): { start: number; end: number } {
  const lineStart = Math.max(text.lastIndexOf('\n', range.start - 1) + 1, 0);
  const prefix = text.slice(lineStart, range.start);

  if (/^(?:[ \t]{0,3}(?:>\s*)?)*(?:[-+*]\s+|\d{1,3}[.)]\s+|[a-zA-Z][.)]\s+|\[[ xX]\]\s+)$/.test(prefix)) {
    return { start: lineStart, end: range.end };
  }

  return range;
}

function getMarkdownRenderedReplacementRange(text: string, selectedText: string): { start: number; end: number } | null {
  return getMarkdownRawSelectionRange(text, selectedText);
}

function replaceSelectedText(
  draftText: string,
  selectedText: string,
  replacement: string,
  beforeContext: string,
  afterContext: string,
): string | null {
  const range = getSelectedTextReplacementRange(draftText, selectedText, beforeContext, afterContext);
  if (!range) return null;

  return `${draftText.slice(0, range.start)}${replacement}${draftText.slice(range.end)}`;
}

function getSelectedTextReplacementRange(
  draftText: string,
  selectedText: string,
  beforeContext: string,
  afterContext: string,
): { start: number; end: number } | null {
  const indexes: number[] = [];
  let searchIndex = draftText.indexOf(selectedText);

  while (searchIndex !== -1) {
    indexes.push(searchIndex);
    searchIndex = draftText.indexOf(selectedText, searchIndex + selectedText.length);
  }

  if (indexes.length === 0) {
    const markdownRange = getMarkdownRenderedReplacementRange(draftText, selectedText);
    if (markdownRange) {
      return markdownRange;
    }

    const range = getTextRange(draftText, selectedText);
    return range;
  }

  const beforeTail = beforeContext.slice(-160);
  const afterHead = afterContext.slice(0, 160);
  const bestIndex = indexes
    .map(index => {
      const before = draftText.slice(Math.max(0, index - beforeTail.length), index);
      const after = draftText.slice(index + selectedText.length, index + selectedText.length + afterHead.length);
      const score = (beforeTail && before.endsWith(beforeTail) ? 2 : 0)
        + (afterHead && after.startsWith(afterHead) ? 2 : 0);
      return { index, score };
    })
    .sort((a, b) => b.score - a.score)[0].index;

  return { start: bestIndex, end: bestIndex + selectedText.length };
}

function buildPatchComparisonMarkdown(
  draftText: string,
  edit: DraftPatchEdit,
  range: { start: number; end: number },
): string | null {
  const before = escapeHtml(draftText.slice(0, range.start));
  const selected = escapeHtml(draftText.slice(range.start, range.end));
  const after = escapeHtml(draftText.slice(range.end));

  if (edit.operation === 'replace') {
    const localizedDiff = buildReviewDiffMarkdown(draftText.slice(range.start, range.end), edit.replace ?? '');
    return [
      before,
      localizedDiff || [
        selected ? `<del data-legal-draft-change="remove">${selected}</del>` : '',
        edit.replace ? `<ins data-legal-draft-change="add">${escapeHtml(edit.replace)}</ins>` : '',
      ].join(''),
      after,
    ].join('');
  }

  if (edit.operation === 'delete') {
    return [
      before,
      selected ? `<del data-legal-draft-change="remove">${selected}</del>` : '',
      after,
    ].join('');
  }

  if (edit.operation === 'insert_before') {
    return [
      before,
      edit.text ? `<ins data-legal-draft-change="add">${escapeHtml(edit.text)}</ins>` : '',
      selected,
      after,
    ].join('');
  }

  if (edit.operation === 'insert_after') {
    return [
      before,
      selected,
      edit.text ? `<ins data-legal-draft-change="add">${escapeHtml(edit.text)}</ins>` : '',
      after,
    ].join('');
  }

  return null;
}

function decodeChangeLogHtml(value: string): string {
  return value
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function normalizeChangeLogText(value: string): string {
  return collapseWhitespace(
    stripAngleBracketMarkup(prepareDraftPreview(value), ' ')
      .replace(/^\s{0,3}(?:[-+*]|\d{1,3}[.)]|[a-zA-Z][.)])\s+/gm, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      .replace(/`([^`]+)`/g, '$1'),
  ).toLowerCase();
}

function getComparableChangeWords(value: string): string[] {
  return Array.from(new Set(
    normalizeChangeLogText(value)
      .match(/[a-z0-9§]+/gi)
      ?.map(word => word.toLowerCase())
      .filter(word => word.length > 2) || [],
  ));
}

function getChangeWordOverlapRatio(sourceText: string, targetText: string): number {
  const sourceWords = getComparableChangeWords(sourceText);
  if (!sourceWords.length) return 0;

  const targetWords = new Set(getComparableChangeWords(targetText));
  const sharedWordCount = sourceWords.filter(word => targetWords.has(word)).length;
  return sharedWordCount / sourceWords.length;
}

function getDeletedChangeLogText(comparisonMarkdown: string | null | undefined): string {
  if (!comparisonMarkdown) return '';

  return Array.from(comparisonMarkdown.matchAll(/<del\b[^>]*>([\s\S]*?)<\/del>/gi))
    .map(match => stripAngleBracketMarkup(decodeChangeLogHtml(match[1] || ''), ' '))
    .join(' ');
}

function patchCanRemoveSelectedText(response: ArtifactEditResponse): boolean {
  const action = response.action || (response.mode === 'edit' ? 'patch_draft' : undefined);
  return action === 'patch_draft'
    && Array.isArray(response.edits)
    && response.edits.some(edit => edit.operation === 'delete' || edit.operation === 'replace');
}

function patchKeepsSelectionSubstance(response: ArtifactEditResponse, selectedText: string): boolean {
  if (!Array.isArray(response.edits)) return false;
  if (response.edits.some(edit => edit.operation === 'delete')) return false;

  const replacementText = response.edits
    .filter(edit => edit.operation === 'replace')
    .map(edit => edit.replace || '')
    .join(' ');
  if (!replacementText.trim()) return false;

  const normalizedSelection = normalizeChangeLogText(selectedText);
  const normalizedReplacement = normalizeChangeLogText(replacementText);
  if (!normalizedSelection) return true;
  if (normalizedReplacement.includes(normalizedSelection)) return true;

  return getChangeWordOverlapRatio(selectedText, replacementText) >= 0.55;
}

function hasSelectedRemovalMarker(
  comparisonMarkdown: string | null | undefined,
  selectedText: string,
): boolean {
  const deletedText = getDeletedChangeLogText(comparisonMarkdown);
  if (!deletedText.trim()) return false;

  const normalizedDeletedText = normalizeChangeLogText(deletedText);
  const normalizedSelection = normalizeChangeLogText(selectedText);
  if (!normalizedSelection) return true;
  if (normalizedDeletedText.includes(normalizedSelection)) return true;

  return getChangeWordOverlapRatio(selectedText, deletedText) >= 0.45;
}

function shouldRejectUntrackedSelectedRemoval(
  response: ArtifactEditResponse,
  selectedText: string,
  comparisonMarkdown: string | null | undefined,
): boolean {
  if (!patchCanRemoveSelectedText(response)) return false;
  if (patchKeepsSelectionSubstance(response, selectedText)) return false;
  return !hasSelectedRemovalMarker(comparisonMarkdown, selectedText);
}

function applyPatchEditToRange(
  draftText: string,
  edit: DraftPatchEdit,
  range: { start: number; end: number },
): string | null {
  if (edit.operation === 'replace') {
    return `${draftText.slice(0, range.start)}${edit.replace ?? ''}${draftText.slice(range.end)}`;
  }

  if (edit.operation === 'delete') {
    return `${draftText.slice(0, range.start)}${draftText.slice(range.end)}`;
  }

  if (edit.operation === 'insert_before') {
    return `${draftText.slice(0, range.start)}${edit.text ?? ''}${draftText.slice(range.start)}`;
  }

  if (edit.operation === 'insert_after') {
    return `${draftText.slice(0, range.end)}${edit.text ?? ''}${draftText.slice(range.end)}`;
  }

  return null;
}

function getStructuredEditComparisonMarkdown(
  response: ArtifactEditResponse,
  draftText: string,
  selectedText: string,
  beforeContext: string,
  afterContext: string,
): string | null {
  if (response.action !== 'patch_draft' || !Array.isArray(response.edits) || response.edits.length !== 1) {
    return null;
  }

  const edit = response.edits[0];
  const selectedRange = selectedText
    ? getSelectedTextReplacementRange(draftText, selectedText, beforeContext, afterContext)
    : null;
  const range = selectedRange || findDraftPatchRange(draftText, edit.find, selectedText);
  if (!range) return null;

  const comparisonMarkdown = buildPatchComparisonMarkdown(draftText, edit, range);
  return comparisonMarkdown ? prepareDraftPreview(comparisonMarkdown) : null;
}

function debugDraftTextPreview(value: string, limit = 180): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, limit);
}

type DraftPatchEdit = {
  operation: 'replace' | 'delete' | 'insert_before' | 'insert_after';
  find: string;
  replace?: string;
  text?: string;
};

type LegalDraftEditResolution =
  | { kind: 'answer'; answer: string }
  | { kind: 'draft'; draft: string };

type QmeLetterType = 'initial' | 'supplemental' | 'records-transmittal' | 'joint' | 'clarification' | 'objection-response';
type QmePerspective = 'applicant' | 'defense' | 'joint';
type QmeTone = 'neutral' | 'firm' | 'objection-safe';
type QmeClaimStatus = 'accepted' | 'denied' | 'partial' | 'disputed';
type QmePacketStatus = 'not-specified' | 'already-served' | 'serving-now' | 'needs-review';

const qmeLetterTypeOptions: Array<{ value: QmeLetterType; label: string }> = [
  { value: 'initial', label: 'Initial' },
  { value: 'supplemental', label: 'Supplemental' },
  { value: 'records-transmittal', label: 'Records' },
  { value: 'joint', label: 'Joint' },
  { value: 'clarification', label: 'Clarification' },
  { value: 'objection-response', label: 'Objection' },
];

const qmePerspectiveOptions: Array<{ value: QmePerspective; label: string }> = [
  { value: 'applicant', label: 'Applicant' },
  { value: 'defense', label: 'Defense' },
  { value: 'joint', label: 'Joint' },
];

const qmeToneOptions: Array<{ value: QmeTone; label: string }> = [
  { value: 'neutral', label: 'Neutral' },
  { value: 'firm', label: 'Firm' },
  { value: 'objection-safe', label: 'Objection-safe' },
];

const qmeClaimStatusOptions: Array<{ value: QmeClaimStatus; label: string }> = [
  { value: 'accepted', label: 'Accepted' },
  { value: 'denied', label: 'Denied' },
  { value: 'partial', label: 'Partial' },
  { value: 'disputed', label: 'Disputed' },
];

const qmePacketStatusOptions: Array<{ value: QmePacketStatus; label: string }> = [
  { value: 'not-specified', label: 'Not set' },
  { value: 'already-served', label: 'Served' },
  { value: 'serving-now', label: 'Serve now' },
  { value: 'needs-review', label: 'Review' },
];

const qmeIssueOptions: Array<{ label: string; questions: string[]; risk: string }> = [
  {
    label: 'AOE/COE',
    questions: [
      'Is the claimed injury medically consistent with the described work exposure?',
      'Please address industrial causation using reasonable medical probability.',
    ],
    risk: 'Do not decide legal compensability for the evaluator.',
  },
  {
    label: 'Body parts',
    questions: [
      'Which claimed body parts require diagnosis and causation opinions?',
      'Are any alleged body parts unrelated, nonindustrial, or insufficiently supported?',
    ],
    risk: 'Separate accepted, denied, and disputed body parts.',
  },
  {
    label: 'TD / work restrictions',
    questions: [
      'What work restrictions were medically appropriate during each disputed period?',
      'Was the employee temporarily disabled from regular or modified work?',
    ],
    risk: 'Tie questions to medical evidence and dates.',
  },
  {
    label: 'MMI / P&S',
    questions: [
      'Has the employee reached MMI/P&S status, and if so, on what date?',
      'If not MMI/P&S, what treatment or evaluation remains necessary?',
    ],
    risk: 'Avoid forcing a premature P&S conclusion.',
  },
  {
    label: 'Permanent impairment',
    questions: [
      'Please provide impairment under the AMA Guides with the supporting chapter/table references.',
      'If a different impairment analysis is medically appropriate, please explain why.',
    ],
    risk: 'Ask for medical reasoning, not a rating conclusion only.',
  },
  {
    label: 'Apportionment',
    questions: [
      'Is any permanent disability caused by nonindustrial factors, prior injury, or pathology?',
      'Please state apportionment percentages and the medical basis for each factor.',
    ],
    risk: 'Require substantial medical evidence, not speculation.',
  },
  {
    label: 'Future medical',
    questions: [
      'What future medical care is reasonably necessary to cure or relieve the industrial condition?',
      'Please identify frequency, duration, and medical basis where possible.',
    ],
    risk: 'Keep treatment requests medically framed.',
  },
  {
    label: 'Treatment need',
    questions: [
      'Is the disputed treatment medically reasonable and causally related to the industrial injury?',
      'Are there alternative treatment recommendations supported by the record?',
    ],
    risk: 'Do not turn the question into utilization review argument.',
  },
  {
    label: 'Compensable consequence',
    questions: [
      'Is the alleged compensable consequence medically caused by the accepted or claimed injury?',
      'Please identify the mechanism of causation and supporting medical evidence.',
    ],
    risk: 'Avoid assuming the consequence is already established.',
  },
  {
    label: 'Psychiatric causation',
    questions: [
      'Please address psychiatric causation and the relative contribution of industrial and nonindustrial factors.',
      'If applicable, please discuss whether the claimed psychiatric injury is predominantly caused by actual events of employment.',
    ],
    risk: 'Denial reasons must be medically relevant and carefully stated.',
  },
  {
    label: 'Almaraz/Guzman',
    questions: [
      'If strict AMA Guides impairment does not accurately reflect impairment, please explain the medical basis for any alternative analysis.',
      'Please identify the chapter/table/method used and why it is the most accurate measure.',
    ],
    risk: 'Ask for medical reasoning; avoid advocacy shorthand.',
  },
];

function findDraftPatchRange(
  draftText: string,
  find: string,
  fallbackSelectedText?: string,
): { start: number; end: number } | null {
  const candidates = Array.from(new Set([
    find,
    find.trim(),
    fallbackSelectedText,
    fallbackSelectedText?.trim(),
  ].filter((candidate): candidate is string => !!candidate)));

  console.debug('[artifact_edit] findDraftPatchRange candidates', candidates.map(candidate => ({
    length: candidate.length,
    preview: debugDraftTextPreview(candidate),
  })));

  for (const candidate of candidates) {
    const exactIndex = draftText.indexOf(candidate);
    if (exactIndex !== -1) {
      console.debug('[artifact_edit] patch range exact match', {
        start: exactIndex,
        end: exactIndex + candidate.length,
        candidatePreview: debugDraftTextPreview(candidate),
      });
      return { start: exactIndex, end: exactIndex + candidate.length };
    }

    const textRange = getTextRange(draftText, candidate);
    if (textRange) {
      console.debug('[artifact_edit] patch range text match', {
        range: textRange,
        candidatePreview: debugDraftTextPreview(candidate),
      });
      return textRange;
    }

    const markdownRange = getMarkdownRenderedReplacementRange(draftText, candidate);
    if (markdownRange) {
      console.debug('[artifact_edit] patch range markdown-rendered match', {
        range: markdownRange,
        candidatePreview: debugDraftTextPreview(candidate),
      });
      return markdownRange;
    }

    const flexibleRange = getFlexibleTextRange(draftText, candidate);
    if (flexibleRange) {
      console.debug('[artifact_edit] patch range flexible match', {
        range: flexibleRange,
        candidatePreview: debugDraftTextPreview(candidate),
      });
      return flexibleRange;
    }
  }

  console.debug('[artifact_edit] no patch range match', {
    draftLen: draftText.length,
    findLen: find.length,
    findPreview: debugDraftTextPreview(find),
    fallbackSelectedLen: fallbackSelectedText?.length ?? 0,
    fallbackSelectedPreview: debugDraftTextPreview(fallbackSelectedText || ''),
  });
  return null;
}

function applyDraftPatches(
  draftText: string,
  edits: DraftPatchEdit[],
  selectedText?: string,
  beforeContext = '',
  afterContext = '',
): string | null {
  let nextDraftText = draftText;
  const fallbackSelectedText = edits.length === 1 ? selectedText : undefined;
  const singleEdit = edits.length === 1 ? edits[0] : null;

  console.debug('[artifact_edit] applyDraftPatches start', {
    draftLen: draftText.length,
    editCount: edits.length,
    selectedLen: selectedText?.length ?? 0,
    selectedPreview: debugDraftTextPreview(selectedText || ''),
    beforeContextPreview: debugDraftTextPreview(beforeContext),
    afterContextPreview: debugDraftTextPreview(afterContext),
    edits: edits.map(edit => ({
      operation: edit.operation,
      findLen: edit.find?.length ?? 0,
      findPreview: debugDraftTextPreview(edit.find || ''),
      replaceLen: edit.replace?.length ?? 0,
      textLen: edit.text?.length ?? 0,
    })),
  });

  if (singleEdit && selectedText) {
    const selectedRange = getSelectedTextReplacementRange(
      draftText,
      selectedText,
      beforeContext,
      afterContext,
    );
    const selectedDraft = selectedRange
      ? applyPatchEditToRange(draftText, singleEdit, selectedRange)
      : null;

    if (selectedDraft && selectedDraft !== draftText) {
      console.debug('[artifact_edit] applied single edit through selected text', {
        operation: singleEdit.operation,
        selectedLen: selectedText.length,
        replacementLen: singleEdit.replace?.length ?? 0,
        textLen: singleEdit.text?.length ?? 0,
      });
      return selectedDraft;
    }
  }

  for (const edit of edits) {
    const range = findDraftPatchRange(nextDraftText, edit.find, fallbackSelectedText);
    if (!range) {
      if (edits.length === 1 && edit.operation === 'replace' && selectedText) {
        const fallbackDraft = replaceSelectedText(
          draftText,
          selectedText,
          edit.replace ?? '',
          beforeContext,
          afterContext,
        );
        console.debug('[artifact_edit] selection fallback result', {
          success: fallbackDraft !== null,
          replacementLen: edit.replace?.length ?? 0,
        });
        return fallbackDraft;
      }
      console.debug('[artifact_edit] applyDraftPatches failed before applying edit', {
        operation: edit.operation,
        findPreview: debugDraftTextPreview(edit.find),
      });
      return null;
    }

    nextDraftText = applyPatchEditToRange(nextDraftText, edit, range) || nextDraftText;
  }

  console.debug('[artifact_edit] applyDraftPatches success', {
    nextDraftLen: nextDraftText.length,
  });
  if (nextDraftText === draftText) {
    console.debug('[artifact_edit] applyDraftPatches produced no change', {
      draftLen: draftText.length,
    });
    return null;
  }
  return nextDraftText;
}

function resolveArtifactEditResponse(
  response: ArtifactEditResponse,
  currentDraftText: string,
  selectedText: string,
  beforeContext: string,
  afterContext: string,
): LegalDraftEditResolution | null {
  const action = response.action || (response.mode === 'chat' ? 'answer_in_chat' : response.mode === 'edit' ? 'patch_draft' : undefined);

  if (action === 'answer_in_chat' || action === 'answer_question') {
    const answer = response.answer?.trim();
    return answer ? { kind: 'answer', answer } : null;
  }

  if (action === 'revise_draft') {
    const draft = response.draft?.trim();
    return draft ? { kind: 'draft', draft } : null;
  }

  if (action === 'patch_draft' && response.edits?.length) {
    console.debug('[artifact_edit] resolving patch_draft response', {
      status: response._status,
      editCount: response.edits.length,
      action,
    });
    const draft = applyDraftPatches(currentDraftText, response.edits, selectedText, beforeContext, afterContext);
    if (draft === null) {
      console.debug('[artifact_edit] patch_draft resolution returned null', {
        currentDraftLen: currentDraftText.length,
        selectedLen: selectedText.length,
        selectedPreview: debugDraftTextPreview(selectedText),
      });
    }
    return draft === null ? null : { kind: 'draft', draft };
  }

  if (response.mode === 'edit' && Object.prototype.hasOwnProperty.call(response, 'replacement')) {
    const draft = replaceSelectedText(
      currentDraftText,
      selectedText,
      response.replacement?.trim() ?? '',
      beforeContext,
      afterContext,
    );
    return draft === null ? null : { kind: 'draft', draft };
  }

  return null;
}

function buildQmeBuilderInstruction(input: {
  letterType: QmeLetterType;
  perspective: QmePerspective;
  tone: QmeTone;
  issues: string[];
  claimStatus: QmeClaimStatus;
  packetStatus: QmePacketStatus;
  evaluator: string;
  specialty: string;
  examDate: string;
  applicant: string;
  defendant: string;
  claimNumber: string;
  dateOfInjury: string;
  undisputedFacts: string;
  disputedFacts: string;
  applicantPosition: string;
  defensePosition: string;
  records: string;
  excludedFacts: string;
  customQuestions: string;
}): string {
  return [
    'Revise the entire QME/AME medical-legal letter using these structured builder inputs.',
    '',
    `Letter type: ${input.letterType}`,
    `Perspective: ${input.perspective}`,
    `Tone: ${input.tone}`,
    `Claim status: ${input.claimStatus}`,
    `Records packet status: ${input.packetStatus}`,
    '',
    'Privacy-preserving metadata:',
    `Evaluator: ${input.evaluator.trim() || 'Use the generic role "evaluator"; do not add a placeholder.'}`,
    `Specialty: ${input.specialty.trim() || 'Use a generic specialty reference only if needed.'}`,
    `Exam date: ${input.examDate.trim() || 'Omit the exam date unless already present in the draft.'}`,
    `Applicant: ${input.applicant.trim() || 'Use the generic role "Applicant"; do not add a placeholder.'}`,
    `Defendant: ${input.defendant.trim() || 'Use the generic role "Defendant"; do not add a placeholder.'}`,
    `Claim number: ${input.claimNumber.trim() || 'Omit claim numbers.'}`,
    `Date of injury: ${input.dateOfInjury.trim() || 'Omit the date of injury unless already present in the draft.'}`,
    '',
    `Issues to cover: ${input.issues.length ? input.issues.join('; ') : 'Use the existing issues in the draft.'}`,
    input.undisputedFacts.trim() ? `Undisputed facts:\n${input.undisputedFacts.trim()}` : 'Undisputed facts: Use only facts already present in the draft; do not invent missing facts.',
    input.disputedFacts.trim() ? `Disputed facts:\n${input.disputedFacts.trim()}` : 'Disputed facts: Preserve disputed facts as positions, not established truth.',
    input.applicantPosition.trim() ? `Applicant position:\n${input.applicantPosition.trim()}` : 'Applicant position: Include only if supported by the draft or provided facts.',
    input.defensePosition.trim() ? `Defendant position:\n${input.defensePosition.trim()}` : 'Defendant position: Include only if supported by the draft or provided facts.',
    input.records.trim() ? `Records to reference or request:\n${input.records.trim()}` : 'Records: Preserve any existing records section; do not invent records.',
    input.excludedFacts.trim() ? `Do not include or do not state as established:\n${input.excludedFacts.trim()}` : 'Do-not-include limits: Avoid settlement communications, privilege/work-product material, and unsupported factual conclusions.',
    input.customQuestions.trim() ? `Custom evaluator questions:\n${input.customQuestions.trim()}` : 'Custom evaluator questions: Generate concise issue-specific questions when helpful.',
    '',
    'Use a QME/AME position-letter structure, not a demand-letter structure.',
    'The letter should be organized around medical-legal evaluation needs: introduction/purpose, parties and claim status, records packet, neutral factual background, disputed issues, evaluator questions grouped by issue, and closing/service language.',
    'Do not include demand-letter language such as "demand is hereby made," payment demands, threats of penalties, or benefit-notice demands unless the user specifically asked for that separate purpose.',
    'For joint or objection-safe mode, keep questions neutral and avoid argumentative facts, legal conclusions directed to the evaluator, settlement discussion, or improper advocacy.',
    'For applicant or defense perspective, preserve appropriate advocacy while keeping medical-legal questions proper.',
    'State disputed facts as party positions, not as established facts.',
    'If information is missing, use generic roles or omit the detail. Do not use bracketed placeholders or invite the user to provide sensitive real-world identifiers.',
    'Return an edit to the draft unless the instruction is impossible to apply.',
  ].join('\n');
}

const LegalDraftPanel: React.FC<LegalDraftPanelProps> = ({
  artifact,
  apiAddress,
  chatId,
  onClose,
  onChatResponse,
  onUpdateArtifact,
  persistedState,
  persistedStateLoading = false,
  onPersistState,
  chatContext = '',
  showChatSocReferences = true,
  onShowChatSocReferencesChange,
}) => {
  const toast = useToast();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [versions, setVersions] = useState<string[]>([artifact.text]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState<number>(0);
  const [versionComparisonsByIndex, setVersionComparisonsByIndex] = useState<Record<number, string>>({});
  const [draftText, setDraftText] = useState(artifact.text);
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});
  const [draftSelection, setDraftSelection] = useState<DraftSelection | null>(null);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isFieldsOpen, setIsFieldsOpen] = useState(false);
  const [fieldHighlightTarget, setFieldHighlightTarget] = useState<string | null>(null);
  const [fieldFocusTarget, setFieldFocusTarget] = useState<string | null>(null);
  const [changePreviewText, setChangePreviewText] = useState<string | null>(null);
  const [isComparingVersions, setIsComparingVersions] = useState(false);
  const [observation, setObservation] = useState('');
  const [isApplyingObservation, setIsApplyingObservation] = useState(false);
  const [isQmeBuilderOpen, setIsQmeBuilderOpen] = useState(false);
  const [qmeLetterType, setQmeLetterType] = useState<QmeLetterType>('initial');
  const [qmePerspective, setQmePerspective] = useState<QmePerspective>('applicant');
  const [qmeTone, setQmeTone] = useState<QmeTone>('objection-safe');
  const [qmeClaimStatus, setQmeClaimStatus] = useState<QmeClaimStatus>('disputed');
  const [qmePacketStatus, setQmePacketStatus] = useState<QmePacketStatus>('not-specified');
  const [qmeEvaluator, setQmeEvaluator] = useState('');
  const [qmeSpecialty, setQmeSpecialty] = useState('');
  const [qmeExamDate, setQmeExamDate] = useState('');
  const [qmeApplicant, setQmeApplicant] = useState('');
  const [qmeDefendant, setQmeDefendant] = useState('');
  const [qmeClaimNumber, setQmeClaimNumber] = useState('');
  const [qmeDateOfInjury, setQmeDateOfInjury] = useState('');
  const [qmeSelectedIssues, setQmeSelectedIssues] = useState<string[]>([]);
  const [qmeUndisputedFacts, setQmeUndisputedFacts] = useState('');
  const [qmeDisputedFacts, setQmeDisputedFacts] = useState('');
  const [qmeApplicantPosition, setQmeApplicantPosition] = useState('');
  const [qmeDefensePosition, setQmeDefensePosition] = useState('');
  const [qmeRecords, setQmeRecords] = useState('');
  const [qmeExcludedFacts, setQmeExcludedFacts] = useState('');
  const [qmeCustomQuestions, setQmeCustomQuestions] = useState('');
  const [isApplyingQmeBuilder, setIsApplyingQmeBuilder] = useState(false);
  const [isReviewingQmeDraft, setIsReviewingQmeDraft] = useState(false);
  const [isEditSettling, setIsEditSettling] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const documentRef = React.useRef<HTMLDivElement | null>(null);
  const noteInputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const lastDraftSelectionRef = React.useRef<DraftSelection | null>(null);
  const selectionCaptureTimeoutRef = React.useRef<number | null>(null);
  const changePreviewTimeoutRef = React.useRef<number | null>(null);
  const initializedArtifactIdRef = React.useRef<string | null>(null);
  const lastReceivedArtifactTextRef = React.useRef(artifact.text);
  const lastAppliedPersistedStateRef = React.useRef<string | null>(null);
  const canUsePlaceholderFields = artifact.kind !== 'letter';
  const placeholders = useMemo(
    () => canUsePlaceholderFields ? getPlaceholders(draftText) : [],
    [canUsePlaceholderFields, draftText],
  );
  const filledDraftText = useMemo(
    () => canUsePlaceholderFields ? applyPlaceholderReplacements(draftText, placeholderValues) : draftText,
    [canUsePlaceholderFields, draftText, placeholderValues],
  );
  const displayFilledDraftText = useMemo(
    () => showChatSocReferences ? filledDraftText : removeChatSocReferences(filledDraftText),
    [filledDraftText, showChatSocReferences],
  );
  const previewDraftText = useMemo(
    () => canUsePlaceholderFields
      ? applyPlaceholderReplacements(draftText, placeholderValues, fieldHighlightTarget, true)
      : draftText,
    [canUsePlaceholderFields, draftText, fieldHighlightTarget, placeholderValues],
  );
  const previewText = useMemo(() => {
    const preparedPreview = prepareDraftPreview(previewDraftText);
    return showChatSocReferences ? preparedPreview : removeChatSocReferences(preparedPreview);
  }, [previewDraftText, showChatSocReferences]);
  const previousVersionText = versions[currentVersionIndex - 1];
  const canCompareVersion = currentVersionIndex > 0 && typeof previousVersionText === 'string';
  const versionComparisonText = useMemo(() => {
    if (!isComparingVersions || !canCompareVersion) return null;

    const previousPreview = prepareDraftPreview(
      canUsePlaceholderFields ? applyPlaceholderReplacements(previousVersionText, placeholderValues) : previousVersionText,
    );
    const currentPreview = prepareDraftPreview(
      canUsePlaceholderFields ? applyPlaceholderReplacements(draftText, placeholderValues) : draftText,
    );
    const visiblePreviousPreview = showChatSocReferences ? previousPreview : removeChatSocReferences(previousPreview);
    const visibleCurrentPreview = showChatSocReferences ? currentPreview : removeChatSocReferences(currentPreview);
    return buildLocalizedReviewDiffMarkdown(visiblePreviousPreview, visibleCurrentPreview);
  }, [canCompareVersion, canUsePlaceholderFields, draftText, isComparingVersions, placeholderValues, previousVersionText, showChatSocReferences]);
  const isVersionCompareActive = isComparingVersions && canCompareVersion;
  const baseDisplayPreviewText = changePreviewText || (isVersionCompareActive && versionComparisonText ? versionComparisonText : previewText);
  const displayPreviewText = showChatSocReferences ? baseDisplayPreviewText : removeChatSocReferences(baseDisplayPreviewText);
  const openItemCount = useMemo(
    () => placeholders.filter(placeholder => !placeholderValues[placeholder]?.trim()).length,
    [placeholderValues, placeholders],
  );
  const completedItemCount = placeholders.length - openItemCount;
  const fieldsProgress = placeholders.length ? (completedItemCount / placeholders.length) * 100 : 0;
  const nextOpenItem = placeholders.find(placeholder => !placeholderValues[placeholder]?.trim());
  const hasUsableDraft = true;
  const qmePrimaryActionLabel = 'Apply Builder';
  const isQmeBuilderAvailable = false;
  const qmeBuilderSummary = useMemo(() => {
    const selectedCount = qmeSelectedIssues.length;
    const packet = qmePacketStatus === 'not-specified' ? 'packet not set' : qmePacketStatus.replace('-', ' ');
    if (selectedCount === 0) return `${qmeClaimStatus} · ${packet}`;
    if (selectedCount === 1) return `${qmeSelectedIssues[0]} · ${packet}`;
    return `${selectedCount} issues · ${packet}`;
  }, [qmeClaimStatus, qmePacketStatus, qmeSelectedIssues]);
  const selectedQmeIssues = useMemo(
    () => qmeIssueOptions.filter(issue => qmeSelectedIssues.includes(issue.label)),
    [qmeSelectedIssues],
  );
  const qmeRiskItems = useMemo(() => [
    {
      label: 'Evaluator identified',
      done: !!qmeEvaluator.trim(),
      detail: qmeEvaluator.trim() || 'Missing evaluator name',
    },
    {
      label: 'Records packet status',
      done: qmePacketStatus !== 'not-specified',
      detail: qmePacketStatus === 'not-specified' ? 'Not set' : qmePacketStatus.replace('-', ' '),
    },
    {
      label: 'Issues selected',
      done: qmeSelectedIssues.length > 0,
      detail: qmeSelectedIssues.length > 0 ? `${qmeSelectedIssues.length} issue${qmeSelectedIssues.length > 1 ? 's' : ''}` : 'No issue groups selected',
    },
    {
      label: 'Disputed facts separated',
      done: !!qmeDisputedFacts.trim() || !!qmeExcludedFacts.trim() || qmeTone === 'objection-safe' || qmePerspective === 'joint',
      detail: qmeDisputedFacts.trim() || qmeExcludedFacts.trim() ? 'Separated in builder' : 'Use caution',
    },
  ], [qmeDisputedFacts, qmeEvaluator, qmeExcludedFacts, qmePacketStatus, qmePerspective, qmeSelectedIssues.length, qmeTone]);

  const persistDraftSessionState = React.useCallback((next?: {
    versions?: string[];
    currentVersionIndex?: number;
    placeholderValues?: Record<string, string>;
    versionComparisonsByIndex?: Record<number, string>;
  }) => {
    if (!onPersistState) return;

    const nextVersions = (next?.versions || versions).filter(version => typeof version === 'string');
    if (nextVersions.length === 0) return;

    const nextVersionIndex = Math.min(
      Math.max(next?.currentVersionIndex ?? currentVersionIndex, 0),
      nextVersions.length - 1,
    );

    onPersistState({
      versions: nextVersions,
      currentVersionIndex: nextVersionIndex,
      fields: canUsePlaceholderFields
        ? fieldsFromPlaceholderValues(next?.placeholderValues || placeholderValues)
        : [],
      versionComparisons: next?.versionComparisonsByIndex || versionComparisonsByIndex,
      updatedAt: Date.now(),
    });
  }, [canUsePlaceholderFields, currentVersionIndex, onPersistState, placeholderValues, versionComparisonsByIndex, versions]);

  const panelBg = useColorModeValue('#ffffff', 'gray.850');
  const borderColor = useColorModeValue('rgba(23, 64, 78, 0.14)', 'whiteAlpha.200');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('#17404e', '#91E4DD');
  const chipBorder = useColorModeValue('rgba(23, 64, 78, 0.10)', 'whiteAlpha.200');
  const actionHover = useColorModeValue('gray.100', 'whiteAlpha.100');
  const typePillColor = useColorModeValue('#7f4031', 'gray.300');
  const inputFocusBorder = useColorModeValue('#17404e', '#91E4DD');
  const filledColor = useColorModeValue('#247063', '#91E4DD');
  const fieldHintBg = useColorModeValue('rgba(23, 64, 78, 0.055)', 'whiteAlpha.100');
  const fieldHintHover = useColorModeValue('rgba(23, 64, 78, 0.10)', 'whiteAlpha.200');
  const fieldRowBorder = useColorModeValue('rgba(23, 64, 78, 0.10)', 'whiteAlpha.100');
  const fieldInputBg = useColorModeValue('rgba(255, 255, 255, 0.94)', 'rgba(26, 32, 44, 0.82)');
  const fieldCardBg = useColorModeValue('rgba(248, 250, 251, 0.86)', 'whiteAlpha.50');
  const fieldCardHover = useColorModeValue('rgba(244, 248, 248, 0.98)', 'whiteAlpha.100');
  const fieldPanelHeaderBg = useColorModeValue('rgba(248, 250, 251, 0.82)', 'rgba(17, 24, 39, 0.72)');
  const missingFieldBg = useColorModeValue('rgba(184, 138, 42, 0.075)', 'rgba(145, 228, 221, 0.08)');
  const missingFieldHoverBg = useColorModeValue('rgba(184, 138, 42, 0.13)', 'rgba(145, 228, 221, 0.14)');
  const missingFieldRing = useColorModeValue('rgba(127, 64, 49, 0.42)', 'rgba(145, 228, 221, 0.42)');
  const fieldPanelBg = useColorModeValue('rgba(255, 255, 255, 0.98)', 'rgba(26, 32, 44, 0.98)');
  const fieldPanelShadow = useColorModeValue('0 18px 48px rgba(23, 64, 78, 0.16)', '0 18px 48px rgba(0, 0, 0, 0.40)');
  const progressTrack = useColorModeValue('rgba(23, 64, 78, 0.10)', 'whiteAlpha.200');
  const popoverBg = useColorModeValue('white', 'gray.800');
  const popoverShadow = useColorModeValue('0 18px 50px rgba(23, 64, 78, 0.18)', '0 18px 50px rgba(0, 0, 0, 0.42)');
  const editSegmentBg = useColorModeValue('#17404e', 'rgba(145, 228, 221, 0.24)');
  const editSegmentHoverBg = useColorModeValue('#123541', 'rgba(145, 228, 221, 0.34)');
  const editSegmentColor = useColorModeValue('white', '#DFFFFC');
  const editSegmentBorder = useColorModeValue('transparent', 'rgba(145, 228, 221, 0.48)');
  const editSegmentFocusRing = useColorModeValue('rgba(23, 64, 78, 0.34)', 'rgba(145, 228, 221, 0.42)');
  const editSegmentShadow = useColorModeValue(
    '0 14px 34px rgba(23, 64, 78, 0.18)',
    '0 18px 42px rgba(0, 0, 0, 0.38), 0 1px 0 rgba(255, 255, 255, 0.18) inset, 0 -1px 0 rgba(0, 0, 0, 0.22) inset',
  );
  const selectedTextBg = useColorModeValue('rgba(255, 193, 14, 0.32)', 'rgba(145, 228, 221, 0.26)');
  const selectedTextRing = useColorModeValue('rgba(255, 193, 14, 0.62)', 'rgba(145, 228, 221, 0.55)');
  const changeAddBg = useColorModeValue('rgba(36, 112, 99, 0.14)', 'rgba(145, 228, 221, 0.16)');
  const changeAddBorder = useColorModeValue('rgba(36, 112, 99, 0.38)', 'rgba(145, 228, 221, 0.46)');
  const changeRemoveBg = useColorModeValue('rgba(127, 64, 49, 0.12)', 'rgba(255, 150, 150, 0.16)');
  const changeRemoveBorder = useColorModeValue('rgba(127, 64, 49, 0.34)', 'rgba(255, 150, 150, 0.42)');
  const documentText = useColorModeValue('#20343c', 'gray.100');
  const documentMutedRule = useColorModeValue('rgba(127, 64, 49, 0.16)', 'whiteAlpha.200');
  const documentMarginRule = useColorModeValue('rgba(184, 138, 42, 0.22)', 'rgba(145, 228, 221, 0.18)');
  const quoteBg = useColorModeValue('rgba(184, 138, 42, 0.045)', 'whiteAlpha.100');
  const toggleBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const qmePanelBg = useColorModeValue('rgba(248, 250, 251, 0.78)', 'rgba(17, 24, 39, 0.72)');
  const qmeExpandedBg = useColorModeValue('rgba(255, 255, 255, 0.86)', 'rgba(26, 32, 44, 0.88)');
  const qmeControlBg = useColorModeValue('rgba(255, 255, 255, 0.88)', 'whiteAlpha.100');
  const qmeControlActiveBg = useColorModeValue('rgba(23, 64, 78, 0.10)', 'rgba(145, 228, 221, 0.16)');
  const qmeControlBorder = useColorModeValue('rgba(23, 64, 78, 0.12)', 'whiteAlpha.200');
  const qmeTextareaBg = useColorModeValue('rgba(255, 255, 255, 0.94)', 'rgba(17, 24, 39, 0.56)');
  const qmeSectionBg = 'transparent';
  const qmeSectionBorder = useColorModeValue('rgba(23, 64, 78, 0.12)', 'rgba(255, 255, 255, 0.10)');

  useEffect(() => {
    if (initializedArtifactIdRef.current === artifact.id) {
      if (artifact.text === lastReceivedArtifactTextRef.current) return;

      const previousArtifactText = lastReceivedArtifactTextRef.current;
      lastReceivedArtifactTextRef.current = artifact.text;

      setDraftText(current => current === previousArtifactText ? artifact.text : current);
      setVersions(current => {
        if (current.length === 1 && current[0] === previousArtifactText) return [artifact.text];
        return current;
      });
      return;
    }

    initializedArtifactIdRef.current = artifact.id;
    lastReceivedArtifactTextRef.current = artifact.text;
    lastAppliedPersistedStateRef.current = null;

    setVersions([artifact.text]);
    setVersionComparisonsByIndex({});
    setCurrentVersionIndex(0);
    setDraftText(artifact.text);
    setPlaceholderValues({});
    setDraftSelection(null);
    setIsNoteOpen(false);
    setIsFieldsOpen(false);
    setFieldHighlightTarget(null);
    setFieldFocusTarget(null);
    setChangePreviewText(null);
    setObservation('');
    setIsQmeBuilderOpen(false);
    setQmeLetterType('initial');
    setQmePerspective('applicant');
    setQmeTone('objection-safe');
    setQmeClaimStatus('disputed');
    setQmePacketStatus('not-specified');
    setQmeEvaluator('');
    setQmeSpecialty('');
    setQmeExamDate('');
    setQmeApplicant('');
    setQmeDefendant('');
    setQmeClaimNumber('');
    setQmeDateOfInjury('');
    setQmeSelectedIssues([]);
    setQmeUndisputedFacts('');
    setQmeDisputedFacts('');
    setQmeApplicantPosition('');
    setQmeDefensePosition('');
    setQmeRecords('');
    setQmeExcludedFacts('');
    setQmeCustomQuestions('');
    setIsApplyingQmeBuilder(false);
    setIsReviewingQmeDraft(false);
    setIsEditSettling(false);
    if (changePreviewTimeoutRef.current !== null) {
      window.clearTimeout(changePreviewTimeoutRef.current);
      changePreviewTimeoutRef.current = null;
    }
  }, [artifact.id, artifact.text]);

  useEffect(() => {
    if (!persistedState) return;

    const persistedVersions = Array.isArray(persistedState.versions)
      ? persistedState.versions.filter(version => typeof version === 'string')
      : [];
    if (persistedVersions.length === 0) return;

    const stateSignature = JSON.stringify({
      artifactId: artifact.id,
      updatedAt: persistedState.updatedAt || null,
      currentVersionIndex: persistedState.currentVersionIndex,
      versions: persistedVersions,
      versionComparisons: persistedState.versionComparisons || {},
      fields: persistedState.fields || [],
    });

    if (lastAppliedPersistedStateRef.current === stateSignature) return;
    lastAppliedPersistedStateRef.current = stateSignature;

    const restoredVersionIndex = Math.min(
      Math.max(persistedState.currentVersionIndex || 0, 0),
      persistedVersions.length - 1,
    );

    setVersions(persistedVersions);
    const persistedVersionComparisons = Object.entries(persistedState.versionComparisons || {})
      .reduce<Record<number, string>>((comparisons, [index, comparison]) => {
        const numericIndex = Number(index);
        if (Number.isInteger(numericIndex) && typeof comparison === 'string') {
          comparisons[numericIndex] = comparison;
        }
        return comparisons;
      }, {});
    setVersionComparisonsByIndex(persistedVersionComparisons);
    setCurrentVersionIndex(restoredVersionIndex);
    setDraftText(persistedVersions[restoredVersionIndex]);
    setPlaceholderValues(canUsePlaceholderFields ? placeholderValuesFromFields(persistedState.fields) : {});
    setChangePreviewText(null);
  }, [artifact.id, canUsePlaceholderFields, persistedState]);

  useEffect(() => {
    if (!canCompareVersion && isComparingVersions) {
      setIsComparingVersions(false);
    }
  }, [canCompareVersion, isComparingVersions]);

  useEffect(() => {
    return () => {
      if (changePreviewTimeoutRef.current !== null) {
        window.clearTimeout(changePreviewTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!fieldHighlightTarget) return;

    const timeout = window.setTimeout(() => {
      const highlightedField = documentRef.current?.querySelector('[data-legal-draft-field-highlight="true"]');
      highlightedField?.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }, 40);

    return () => window.clearTimeout(timeout);
  }, [fieldHighlightTarget, previewText, prefersReducedMotion]);

  useEffect(() => {
    if (!fieldFocusTarget || !isFieldsOpen) return;

    const timeout = window.setTimeout(() => {
      const fieldIndex = placeholders.indexOf(fieldFocusTarget);
      const fieldInput = fieldIndex >= 0
        ? document.getElementById(`legal-draft-field-${artifact.id}-${fieldIndex}`) as HTMLInputElement | null
        : null;

      fieldInput?.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
      fieldInput?.focus();
      setFieldFocusTarget(null);
    }, 60);

    return () => window.clearTimeout(timeout);
  }, [artifact.id, fieldFocusTarget, isFieldsOpen, placeholders, prefersReducedMotion]);

  const pushNewVersion = (newText: string, comparisonMarkdown?: string | null) => {
    if (newText === draftText) return;

    const nextVersions = [...versions.slice(0, currentVersionIndex + 1), newText];
    const nextVersionIndex = nextVersions.length - 1;
    const nextVersionComparisons = Object.entries(versionComparisonsByIndex)
      .reduce<Record<number, string>>((comparisons, [index, comparison]) => {
        const numericIndex = Number(index);
        if (Number.isInteger(numericIndex) && numericIndex <= currentVersionIndex) {
          comparisons[numericIndex] = comparison;
        }
        return comparisons;
      }, {});
    if (comparisonMarkdown) {
      nextVersionComparisons[nextVersionIndex] = comparisonMarkdown;
    }
    setVersions(nextVersions);
    setVersionComparisonsByIndex(nextVersionComparisons);
    setCurrentVersionIndex(nextVersionIndex);
    setDraftText(newText);
    persistDraftSessionState({
      versions: nextVersions,
      currentVersionIndex: nextVersionIndex,
      versionComparisonsByIndex: nextVersionComparisons,
    });
    onUpdateArtifact?.(newText);
  };

  const handleVersionChange = (index: number) => {
    if (index < 0 || index >= versions.length) return;
    setCurrentVersionIndex(index);
    setDraftText(versions[index]);
    setChangePreviewText(null);
    persistDraftSessionState({ currentVersionIndex: index });
    onUpdateArtifact?.(versions[index]);
  };

  const startChangePreview = (previousDraftText: string, nextDraftText: string, comparisonMarkdown?: string | null) => {
    if (changePreviewTimeoutRef.current !== null) {
      window.clearTimeout(changePreviewTimeoutRef.current);
      changePreviewTimeoutRef.current = null;
    }

    const reviewMarkdown = comparisonMarkdown || buildLocalizedReviewDiffMarkdown(
      prepareDraftPreview(previousDraftText),
      prepareDraftPreview(nextDraftText),
    ) || null;
    setChangePreviewText(reviewMarkdown);

    if (reviewMarkdown) {
      changePreviewTimeoutRef.current = window.setTimeout(() => {
        setChangePreviewText(null);
        changePreviewTimeoutRef.current = null;
      }, prefersReducedMotion ? 2200 : 4200);
    }
  };

  useEffect(() => {
    if (!isNoteOpen) return;
    window.setTimeout(() => noteInputRef.current?.focus(), 0);
  }, [isNoteOpen]);

  const reviewUnresolvedFields = (action: string, allowProceed = false) => {
    setIsFieldsOpen(true);
    if (nextOpenItem) setFieldHighlightTarget(nextOpenItem);

    toast({
      position: 'top',
      title: `${openItemCount} field${openItemCount > 1 ? 's' : ''} still need attention`,
      description: allowProceed
        ? `The ${action} will continue with placeholders left in the draft.`
        : `Review the highlighted field before you ${action}.`,
      status: 'warning',
      duration: 4500,
      isClosable: true,
    });
  };

  const downloadDraft = async () => {
    if (!hasUsableDraft) {
      toast({
        position: 'top',
        title: 'Generate the letter first',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (openItemCount > 0) {
      reviewUnresolvedFields('PDF download', true);
    }

    setIsDownloadingPdf(true);
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
      const margin = 72;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const maxWidth = pageWidth - margin * 2;
      const lineHeight = 15;
      let y = margin;

      pdf.setFont('times', 'normal');
      pdf.setFontSize(12);

      stripMarkdownForPdf(displayFilledDraftText).split('\n').forEach(rawLine => {
        const line = rawLine.trimEnd();
        const wrappedLines = line ? pdf.splitTextToSize(line, maxWidth) : [''];

        wrappedLines.forEach((wrappedLine: string) => {
          if (y > pageHeight - margin) {
            pdf.addPage();
            y = margin;
          }

          pdf.text(wrappedLine, margin, y);
          y += line ? lineHeight : lineHeight * 0.7;
        });
      });

      pdf.save(getPdfFileName(artifact.title));

      toast({
        position: 'top',
        title: 'PDF downloaded',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        position: 'top',
        title: 'Could not create PDF',
        description: error?.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const copyDraft = async () => {
    if (!hasUsableDraft) {
      toast({
        position: 'top',
        title: 'Generate the letter first',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (openItemCount > 0) {
      reviewUnresolvedFields('copy the draft');
      return;
    }

    await navigator.clipboard.writeText(displayFilledDraftText);
    toast({
      position: 'top',
      title: 'Draft copied',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const getDraftSelection = (selection: Selection | null): DraftSelection | null => {
    const renderedSelectedText = selection?.toString().trim() || '';

    if (!selection || selection.rangeCount === 0 || !renderedSelectedText || !documentRef.current) {
      return null;
    }

    const range = selection.getRangeAt(0);
    const anchorNode = selection.anchorNode;
    const focusNode = selection.focusNode;
    const isInsideDocument = !!anchorNode
      && !!focusNode
      && documentRef.current.contains(anchorNode)
      && documentRef.current.contains(focusNode);

    if (!isInsideDocument) {
      return null;
    }

    const renderedText = documentRef.current.innerText || previewText;
    const rawSelectionRange = getMarkdownRawSelectionRange(filledDraftText, renderedSelectedText);
    const sourceSelectionRange = canUsePlaceholderFields
      ? getDraftSourceSelectionRange(draftText, renderedSelectedText, placeholderValues)
      : null;
    const selectedText = rawSelectionRange
      ? filledDraftText.slice(rawSelectionRange.start, rawSelectionRange.end).trim()
      : renderedSelectedText;
    const sourceSelectedText = sourceSelectionRange
      ? draftText.slice(sourceSelectionRange.start, sourceSelectionRange.end).trim()
      : selectedText;
    const sourceSelectedIndex = sourceSelectionRange?.start ?? 0;
    const sourceSelectedEnd = sourceSelectionRange?.end ?? sourceSelectedIndex + sourceSelectedText.length;
    console.debug('[artifact_edit] captured draft selection', {
      renderedLen: renderedSelectedText.length,
      rawLen: selectedText.length,
      sourceLen: sourceSelectedText.length,
      usedRawMarkdown: !!rawSelectionRange,
      usedSourceRange: !!sourceSelectionRange,
      renderedPreview: debugDraftTextPreview(renderedSelectedText),
      rawPreview: debugDraftTextPreview(selectedText),
      sourcePreview: debugDraftTextPreview(sourceSelectedText),
    });
    const selectedRange = getTextRange(renderedText, renderedSelectedText);
    const selectedIndex = selectedRange?.start ?? 0;
    const selectedEnd = selectedRange?.end ?? selectedIndex + renderedSelectedText.length;
    const rect = range.getBoundingClientRect();
    const rects = getRangeSelectionRects(range, documentRef.current);

    return {
      selectedText,
      beforeContext: renderedText.slice(Math.max(0, selectedIndex - 900), selectedIndex),
      afterContext: renderedText.slice(selectedEnd, selectedEnd + 900),
      sourceSelectedText,
      sourceBeforeContext: draftText.slice(Math.max(0, sourceSelectedIndex - 900), sourceSelectedIndex),
      sourceAfterContext: draftText.slice(sourceSelectedEnd, sourceSelectedEnd + 900),
      rects,
      x: Math.min(Math.max(rect.left + rect.width / 2, 170), window.innerWidth - 170),
      y: Math.min(Math.max(rect.bottom + 10, 72), window.innerHeight - 80),
      viewportTop: rect.top,
      viewportBottom: rect.bottom,
    };
  };

  const captureSelection = () => {
    const currentSelection = getDraftSelection(window.getSelection());
    const nextSelection = currentSelection || lastDraftSelectionRef.current;

    if (!nextSelection || isNoteOpen) return;

    lastDraftSelectionRef.current = nextSelection;
    setDraftSelection(nextSelection);
    setIsNoteOpen(false);
    setObservation('');
    window.getSelection()?.removeAllRanges();
  };

  const scheduleSelectionCapture = (delay = 40) => {
    if (selectionCaptureTimeoutRef.current !== null) {
      window.clearTimeout(selectionCaptureTimeoutRef.current);
    }

    selectionCaptureTimeoutRef.current = window.setTimeout(() => {
      captureSelection();
      selectionCaptureTimeoutRef.current = null;
    }, delay);
  };

  const beginSelection = (event?: React.MouseEvent<HTMLDivElement>) => {
    if (isNoteOpen || isApplyingObservation) return;
    if (event && event.detail > 1) return;

    lastDraftSelectionRef.current = null;
    setDraftSelection(null);
    setFieldHighlightTarget(null);
    setObservation('');
  };

  const handleDraftMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    scheduleSelectionCapture(event.detail > 1 ? 120 : 40);
  };

  const handleDraftDoubleClick = () => {
    scheduleSelectionCapture(140);
  };

  const openFieldFromDraft = (placeholder: string) => {
    setDraftSelection(null);
    setIsNoteOpen(false);
    setObservation('');
    setIsFieldsOpen(true);
    setFieldHighlightTarget(placeholder);
    setFieldFocusTarget(placeholder);
  };

  const handleDraftClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const fieldElement = (event.target as HTMLElement).closest('[data-legal-draft-missing-field="true"]') as HTMLElement | null;
    const placeholder = fieldElement?.dataset.placeholder;
    if (!placeholder) return;

    event.preventDefault();
    openFieldFromDraft(placeholder);
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      const nextSelection = getDraftSelection(window.getSelection());
      if (nextSelection) {
        lastDraftSelectionRef.current = nextSelection;
      }
    };

    const handleSelectionRelease = () => {
      scheduleSelectionCapture();
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mouseup', handleSelectionRelease);
    document.addEventListener('pointerup', handleSelectionRelease);
    document.addEventListener('touchend', handleSelectionRelease);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleSelectionRelease);
      document.removeEventListener('pointerup', handleSelectionRelease);
      document.removeEventListener('touchend', handleSelectionRelease);
      if (selectionCaptureTimeoutRef.current !== null) {
        window.clearTimeout(selectionCaptureTimeoutRef.current);
      }
    };
  });

  const cancelObservation = () => {
    setDraftSelection(null);
    setIsNoteOpen(false);
    setObservation('');
    window.getSelection()?.removeAllRanges();
  };

  const submitObservation = async () => {
    if (!draftSelection || !observation.trim() || isApplyingObservation) return;

    setIsApplyingObservation(true);
    const editingStartedAt = Date.now();
    try {
      const currentDraftText = filledDraftText;
      const response = await fetchArtifactEdit(apiAddress, {
        chatId,
        draftText: currentDraftText,
        selectedText: draftSelection.selectedText,
        beforeContext: draftSelection.beforeContext,
        afterContext: draftSelection.afterContext,
        observation: observation.trim(),
      });

      if (!response || response._status >= 400) {
        console.debug('[artifact_edit] backend error response', response);
        throw new Error(response?.error || 'Unable to apply note');
      }

      console.debug('[artifact_edit] backend success response', {
        status: response._status,
        action: response.action,
        mode: response.mode,
        editCount: response.edits?.length ?? 0,
        draftLen: response.draft?.length ?? 0,
        replacementLen: response.replacement?.length ?? 0,
        answerLen: response.answer?.length ?? 0,
      });

      const sourceSelectedText = draftSelection.sourceSelectedText;
      let resolutionSelectedText = sourceSelectedText || draftSelection.selectedText;
      let resolutionBeforeContext = sourceSelectedText ? (draftSelection.sourceBeforeContext || '') : draftSelection.beforeContext;
      let resolutionAfterContext = sourceSelectedText ? (draftSelection.sourceAfterContext || '') : draftSelection.afterContext;
      let resolutionBaseText = sourceSelectedText ? draftText : currentDraftText;
      let resolution = sourceSelectedText
        ? resolveArtifactEditResponse(
          response,
          draftText,
          sourceSelectedText,
          draftSelection.sourceBeforeContext || '',
          draftSelection.sourceAfterContext || '',
        )
        : null;

      if (!resolution) {
        if (draftSelection.sourceSelectedText) {
          console.debug('[artifact_edit] retrying resolution against visible draft selection', {
            selectedTextPreview: debugDraftTextPreview(draftSelection.selectedText),
            sourceSelectedTextPreview: debugDraftTextPreview(sourceSelectedText || ''),
          });
        }
        resolution = resolveArtifactEditResponse(
          response,
          currentDraftText,
          draftSelection.selectedText,
          draftSelection.beforeContext,
          draftSelection.afterContext,
        );
        if (resolution) {
          resolutionBaseText = currentDraftText;
          resolutionSelectedText = draftSelection.selectedText;
          resolutionBeforeContext = draftSelection.beforeContext;
          resolutionAfterContext = draftSelection.afterContext;
        }
      } else {
        console.debug('[artifact_edit] applied resolution against source draft selection', {
          selectedTextPreview: debugDraftTextPreview(draftSelection.selectedText),
          sourceSelectedTextPreview: debugDraftTextPreview(sourceSelectedText || ''),
        });
      }

      if (!resolution) {
        console.debug('[artifact_edit] no resolution produced', {
          response,
          selectedTextPreview: debugDraftTextPreview(draftSelection.selectedText),
          beforeContextPreview: debugDraftTextPreview(draftSelection.beforeContext),
          afterContextPreview: debugDraftTextPreview(draftSelection.afterContext),
        });
        throw new Error('The requested edit could not be applied');
      }

      if (resolution.kind === 'answer') {
        onChatResponse?.(observation.trim(), resolution.answer);
      } else {
        const minimumEditingMs = 750;
        const elapsedEditingMs = Date.now() - editingStartedAt;
        if (elapsedEditingMs < minimumEditingMs) {
          await delay(minimumEditingMs - elapsedEditingMs);
        }

        const comparisonMarkdown = getStructuredEditComparisonMarkdown(
          response,
          resolutionBaseText,
          resolutionSelectedText,
          resolutionBeforeContext,
          resolutionAfterContext,
        );

        if (shouldRejectUntrackedSelectedRemoval(
          response,
          resolutionSelectedText,
          comparisonMarkdown,
        )) {
          throw new Error('The edit would remove the highlighted text without a reliable change log, so I left the draft unchanged.');
        }

        startChangePreview(resolutionBaseText, resolution.draft, comparisonMarkdown);
        pushNewVersion(resolution.draft, comparisonMarkdown);
        setPlaceholderValues({});
        setIsNoteOpen(false);
        setObservation('');
        setIsEditSettling(true);
        window.setTimeout(() => {
          setDraftSelection(null);
          setIsEditSettling(false);
          window.getSelection()?.removeAllRanges();
        }, 950);
      }
      if (resolution.kind === 'answer') {
        cancelObservation();
      }
    } catch (error: any) {
      toast({
        position: 'top',
        title: 'Could not apply note',
        description: error?.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsApplyingObservation(false);
    }
  };

  const toggleQmeIssue = (issue: string) => {
    setQmeSelectedIssues(current => (
      current.includes(issue)
        ? current.filter(selectedIssue => selectedIssue !== issue)
        : [...current, issue]
    ));
  };

  const applyQmeBuilderToDraft = async () => {
    if (isApplyingQmeBuilder) return;

    setIsApplyingQmeBuilder(true);
    const editingStartedAt = Date.now();
    try {
      const isGeneratingInitialLetter = false;
      const currentDraftText = filledDraftText;
      const instruction = `${buildQmeBuilderInstruction({
        letterType: qmeLetterType,
        perspective: qmePerspective,
        tone: qmeTone,
        issues: qmeSelectedIssues,
        claimStatus: qmeClaimStatus,
        packetStatus: qmePacketStatus,
        evaluator: qmeEvaluator,
        specialty: qmeSpecialty,
        examDate: qmeExamDate,
        applicant: qmeApplicant,
        defendant: qmeDefendant,
        claimNumber: qmeClaimNumber,
        dateOfInjury: qmeDateOfInjury,
        undisputedFacts: qmeUndisputedFacts,
        disputedFacts: qmeDisputedFacts,
        applicantPosition: qmeApplicantPosition,
        defensePosition: qmeDefensePosition,
        records: qmeRecords,
        excludedFacts: qmeExcludedFacts,
        customQuestions: qmeCustomQuestions,
      })}`;
      const response = await fetchArtifactEdit(apiAddress, {
        chatId,
        draftText: currentDraftText,
        selectedText: artifact.title || 'QME/AME letter',
        beforeContext: '',
        afterContext: '',
        observation: instruction,
      });

      if (!response || response._status >= 400) {
        throw new Error(response?.error || 'Unable to apply QME/AME builder');
      }

      const resolution = resolveArtifactEditResponse(
        response,
        currentDraftText,
        artifact.title || 'QME/AME letter',
        '',
        '',
      );

      if (!resolution) {
        throw new Error('The QME/AME builder could not update the draft');
      }

      if (resolution.kind === 'answer') {
        onChatResponse?.('QME/AME builder', resolution.answer);
        toast({
          position: 'top',
          title: 'Builder note added to chat',
          status: 'info',
          duration: 3500,
          isClosable: true,
        });
        return;
      }

      const minimumEditingMs = 750;
      const elapsedEditingMs = Date.now() - editingStartedAt;
      if (elapsedEditingMs < minimumEditingMs) {
        await delay(minimumEditingMs - elapsedEditingMs);
      }

      startChangePreview(currentDraftText, resolution.draft);
      pushNewVersion(resolution.draft);
      setPlaceholderValues({});
      toast({
        position: 'top',
        title: isGeneratingInitialLetter ? 'QME/AME letter generated' : 'QME/AME letter updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        position: 'top',
        title: 'Could not apply QME/AME builder',
        description: error?.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsApplyingQmeBuilder(false);
    }
  };

  const reviewQmeAdvocacy = async () => {
    if (isReviewingQmeDraft) return;

    setIsReviewingQmeDraft(true);
    try {
      const reviewPrompt = [
        'Review the entire QME/AME letter for improper advocacy risk.',
        'Answer in chat only. Do not rewrite the draft.',
        'Focus on argumentative facts stated as undisputed, legal conclusions directed to the evaluator, settlement references, privilege/work-product concerns, overly leading questions, missing neutrality for joint letters, and anything likely to draw an objection.',
        'Give concise findings and suggested fixes.',
      ].join('\n');

      const response = await fetchArtifactEdit(apiAddress, {
        chatId,
        draftText: filledDraftText,
        selectedText: artifact.title || 'QME/AME letter',
        beforeContext: '',
        afterContext: '',
        observation: reviewPrompt,
      });

      if (!response || response._status >= 400) {
        throw new Error(response?.error || 'Unable to review QME/AME letter');
      }

      const resolution = resolveArtifactEditResponse(
        response,
        filledDraftText,
        artifact.title || 'QME/AME letter',
        '',
        '',
      );

      if (resolution?.kind === 'answer') {
        onChatResponse?.('Review QME/AME advocacy risk', resolution.answer);
      } else {
        onChatResponse?.(
          'Review QME/AME advocacy risk',
          'The review returned a draft edit instead of a chat note, so I left the letter unchanged. Ask me to apply the suggested changes if you want the draft revised.',
        );
      }
    } catch (error: any) {
      toast({
        position: 'top',
        title: 'Could not review advocacy risk',
        description: error?.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsReviewingQmeDraft(false);
    }
  };

  const findPlaceholderInText = (placeholder: string) => {
    setDraftSelection(null);
    setIsNoteOpen(false);
    setObservation('');
    setIsFieldsOpen(false);
    setFieldHighlightTarget(placeholder);
  };

  const selectionPopoverLayout = draftSelection ? getSelectionPopoverLayout(draftSelection, isNoteOpen) : null;
  const latestVersionIndex = versions.length - 1;
  const hasVersionHistory = versions.length > 1;
  const isViewingLatestVersion = currentVersionIndex === latestVersionIndex;
  const isVersionHistoryLoading = persistedStateLoading && !hasVersionHistory;
  const versionSummary = isComparingVersions && canCompareVersion
    ? `Comparing ${currentVersionIndex} to ${currentVersionIndex + 1} of ${versions.length}`
    : `${isViewingLatestVersion ? 'Latest version' : 'Viewing previous version'} · ${currentVersionIndex + 1} of ${versions.length}`;

  return (
    <Flex
      h="100%"
      minW={0}
      direction="column"
      bg={panelBg}
      borderLeft="1px solid"
      borderColor={borderColor}
      animation={prefersReducedMotion ? undefined : `${panelIn} 180ms ease-out forwards`}
      overflow="hidden"
      position="relative"
    >
      {/* ── Header ── */}
      <Flex
        align="center"
        gap={2}
        h="48px"
        minH="48px"
        px={4}
        borderBottom="1px solid"
        borderColor={borderColor}
        flexShrink={0}
      >
        <Text fontSize="14px" fontWeight={700} color={headingColor} noOfLines={1} flex={1} minW={0}>
          {artifact.title}
        </Text>

        {onShowChatSocReferencesChange && (
          <Tooltip label={showChatSocReferences ? "Hide SOC sources" : "Show SOC sources"} placement="bottom">
            <IconButton
              aria-label="Toggle sources"
              icon={showChatSocReferences ? <FaEyeSlash /> : <FaEye />}
              size="sm"
              variant="ghost"
              color={mutedText}
              borderRadius="md"
              _hover={{ bg: actionHover, color: headingColor }}
              onClick={() => onShowChatSocReferencesChange(!showChatSocReferences)}
            />
          </Tooltip>
        )}
        <Tooltip label="Copy" placement="bottom">
          <IconButton
            aria-label="Copy draft text"
            icon={<FaClipboard />}
            size="sm"
            variant="ghost"
            color={mutedText}
            borderRadius="md"
            isDisabled={!hasUsableDraft}
            _hover={{ bg: actionHover, color: headingColor }}
            onClick={copyDraft}
          />
        </Tooltip>
        <Tooltip label="Download PDF" placement="bottom">
          <IconButton
            aria-label="Download draft as PDF"
            icon={isDownloadingPdf ? <Spinner size="xs" /> : <FaDownload />}
            size="sm"
            variant="ghost"
            color={mutedText}
            borderRadius="md"
            isDisabled={isDownloadingPdf || !hasUsableDraft}
            _hover={{ bg: actionHover, color: headingColor }}
            onClick={downloadDraft}
          />
        </Tooltip>
        <Tooltip label="Close" placement="bottom">
          <IconButton
            aria-label="Close draft"
            icon={<FaTimes />}
            size="sm"
            variant="ghost"
            color={mutedText}
            borderRadius="md"
            _hover={{ bg: actionHover, color: headingColor }}
            onClick={handleClose}
          />
        </Tooltip>
      </Flex>

      {/* ── Fields bar ── */}
      {placeholders.length > 0 && (
        <Flex
          align="center"
          gap={3}
          h="40px"
          minH="40px"
          px={4}
          borderBottom="1px solid"
          borderColor={borderColor}
          flexShrink={0}
        >
          <Flex align="center" justify="center" w="20px" h="20px" color={openItemCount === 0 ? filledColor : mutedText} flexShrink={0} fontSize="13px">
            {openItemCount === 0 ? <FaCheckCircle aria-hidden /> : <FaTasks aria-hidden />}
          </Flex>
          <Flex align="center" gap={2} flex={1} minW={0}>
            <Text fontSize="12px" fontWeight={600} color={headingColor} flexShrink={0}>
              {openItemCount === 0 ? 'All fields filled' : `${openItemCount} field${openItemCount > 1 ? 's' : ''} remaining`}
            </Text>
            <Box flex={1} h="2px" bg={progressTrack} borderRadius="999px" overflow="hidden" minW="40px">
              <Box h="100%" w={`${fieldsProgress}%`} bg={openItemCount === 0 ? filledColor : headingColor} borderRadius="999px" transition="width 160ms ease-out" />
            </Box>
          </Flex>
          <Button
            size="xs"
            variant="ghost"
            color={headingColor}
            fontWeight={600}
            fontSize="12px"
            rightIcon={isFieldsOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
            _hover={{ bg: actionHover }}
            onClick={() => setIsFieldsOpen(current => !current)}
          >
            {isFieldsOpen ? 'Hide' : 'Fill fields'}
          </Button>
        </Flex>
      )}

      {/* ── Fields dropdown ── */}
      {placeholders.length > 0 && isFieldsOpen && (
        <Box
          position="absolute"
          top={placeholders.length > 0 ? '96px' : '56px'}
          left={{ base: 3, md: 4 }}
          right={{ base: 3, md: 4 }}
          zIndex={25}
          bg={fieldPanelBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="8px"
          boxShadow={fieldPanelShadow}
          backdropFilter="blur(18px)"
          overflow="hidden"
        >
          <Flex
            align="center"
            justify="space-between"
            gap={3}
            px={4}
            py={3}
            bg={fieldPanelHeaderBg}
            borderBottom="1px solid"
            borderColor={borderColor}
          >
            <Flex align="center" gap={2.5} minW={0}>
              <Flex
                align="center"
                justify="center"
                w="28px"
                h="28px"
                borderRadius="8px"
                bg={fieldHintBg}
                color={headingColor}
                flexShrink={0}
              >
                <FaTasks size={12} aria-hidden />
              </Flex>
              <Box minW={0}>
                <Text fontSize="13px" fontWeight={750} color={headingColor} noOfLines={1}>
                  Complete draft fields
                </Text>
                <Text fontSize="11px" color={mutedText} noOfLines={1} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {completedItemCount} of {placeholders.length} filled
                </Text>
              </Box>
            </Flex>
            <Box w="86px" h="4px" bg={progressTrack} borderRadius="999px" overflow="hidden" flexShrink={0}>
              <Box h="100%" w={`${fieldsProgress}%`} bg={openItemCount === 0 ? filledColor : headingColor} borderRadius="999px" transition="width 160ms ease-out" />
            </Box>
          </Flex>

          <Box maxH="min(392px, calc(100vh - 236px))" overflowY="auto" px={3} py={3} overscrollBehavior="contain">
            <Flex direction="column" gap={2}>
              {placeholders.map((placeholder, index) => {
                const label = getPlaceholderLabel(placeholder);
                const inputId = `legal-draft-field-${artifact.id}-${index}`;
                const isFilled = !!placeholderValues[placeholder]?.trim();
                return (
                  <Box
                    key={placeholder}
                    bg={isFilled ? fieldHintBg : fieldCardBg}
                    border="1px solid"
                    borderColor={fieldRowBorder}
                    borderRadius="8px"
                    p={3}
                    transition="background-color 140ms ease, border-color 140ms ease"
                    _hover={{
                      bg: isFilled ? fieldHintBg : fieldCardHover,
                    }}
                    _focusWithin={{
                      bg: fieldCardHover,
                      borderColor: inputFocusBorder,
                    }}
                  >
                    <Flex align="center" justify="space-between" gap={3} minW={0} mb={2}>
                      <Text
                        as="label"
                        htmlFor={inputId}
                        display="block"
                        fontSize="11px"
                        fontWeight={750}
                        color={isFilled ? filledColor : headingColor}
                        lineHeight="1.35"
                        textTransform="uppercase"
                        letterSpacing="0"
                        noOfLines={1}
                        minW={0}
                      >
                        {label}
                      </Text>
                      <Flex align="center" gap={1.5} color={isFilled ? filledColor : mutedText} fontSize="11px" fontWeight={700} flexShrink={0}>
                        {isFilled && <Box as={FaCheckCircle} aria-hidden fontSize="11px" />}
                        <Text>{isFilled ? 'Filled' : 'Needed'}</Text>
                      </Flex>
                    </Flex>
                    <Flex align="center" gap={2} minW={0}>
                      <InputGroup size="sm" minW={0}>
                        <Input
                          id={inputId}
                          name={label}
                          value={placeholderValues[placeholder] || ''}
                          onChange={event => {
                            const value = event.target.value;
                            const nextPlaceholderValues = { ...placeholderValues, [placeholder]: value };
                            setPlaceholderValues(nextPlaceholderValues);
                            persistDraftSessionState({ placeholderValues: nextPlaceholderValues });
                          }}
                          fontSize={{ base: '16px', md: '13px' }}
                          bg={fieldInputBg}
                          borderColor={chipBorder}
                          borderRadius="7px"
                          pr="10px"
                          _hover={{ borderColor }}
                          _focusVisible={{
                            borderColor: inputFocusBorder,
                            boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                          }}
                        />
                      </InputGroup>
                      <Tooltip label="Find in draft" placement="top">
                        <IconButton
                          aria-label={`Find ${label} in draft text`}
                          icon={<FaSearch />}
                          size="xs"
                          h="32px"
                          minW="32px"
                          borderRadius="5px"
                          variant="ghost"
                          color={headingColor}
                          bg={fieldHintBg}
                          flexShrink={0}
                          _hover={{ bg: fieldHintHover }}
                          _focusVisible={{
                            boxShadow: `0 0 0 2px ${inputFocusBorder}`,
                          }}
                          onClick={() => findPlaceholderInText(placeholder)}
                        />
                      </Tooltip>
                    </Flex>
                  </Box>
                );
              })}
            </Flex>
          </Box>
        </Box>
      )}

      {/* ── QME/AME builder ── */}
      {isQmeBuilderAvailable && (
        <Box
          flexShrink={0}
          borderBottom="1px solid"
          borderColor={borderColor}
          bg={isQmeBuilderOpen ? qmeExpandedBg : qmePanelBg}
        >
          <Flex
            align="center"
            gap={3}
            minH="44px"
            px={4}
            py={2}
          >
            <Flex
              align="center"
              justify="center"
              w="26px"
              h="26px"
              borderRadius="7px"
              bg={qmeControlActiveBg}
              color={headingColor}
              flexShrink={0}
            >
              <FaMagic size={12} aria-hidden />
            </Flex>
            <Box flex={1} minW={0}>
              <Text fontSize="12px" fontWeight={750} color={headingColor} noOfLines={1}>
                Letter Builder
              </Text>
              <Text fontSize="11px" color={mutedText} noOfLines={1}>
                {qmeLetterType} · {qmePerspective} · {qmeBuilderSummary}
              </Text>
            </Box>
            <Button
              size="xs"
              variant="ghost"
              h={{ base: '36px', md: '30px' }}
              color={headingColor}
              fontWeight={700}
              fontSize="12px"
              rightIcon={isQmeBuilderOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              _hover={{ bg: actionHover }}
              _focusVisible={{
                boxShadow: `0 0 0 2px ${inputFocusBorder}`,
              }}
              onClick={() => setIsQmeBuilderOpen(current => !current)}
            >
              {isQmeBuilderOpen ? 'Hide' : 'Build'}
            </Button>
          </Flex>

          {isQmeBuilderOpen && (
            <Box px={{ base: 3, md: 4 }} pt={2} pb={4} maxH="min(620px, 58vh)" overflowY="auto" overscrollBehavior="contain">
              <Flex direction="column" gap={3}>
                <Box
                  borderTop="1px solid"
                  borderColor={qmeSectionBorder}
                  bg={qmeSectionBg}
                  pt={3}
                >
                  <Text fontSize="11px" fontWeight={750} color={headingColor} mb={2}>
                    Document setup
                  </Text>
                  <Box
                    display="grid"
                    gridTemplateColumns={{ base: '1fr', lg: 'minmax(0, 1.3fr) minmax(220px, 0.85fr)', xl: 'minmax(0, 1.35fr) minmax(220px, 0.85fr) minmax(240px, 0.9fr)' }}
                    gap={3}
                  >
                  <Box minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Letter type
                    </Text>
                    <Select
                      aria-label="QME/AME letter type"
                      name="qme-letter-type"
                      value={qmeLetterType}
                      onChange={event => setQmeLetterType(event.target.value as QmeLetterType)}
                      h={{ base: '40px', md: '34px' }}
                      bg={qmeControlBg}
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      color={headingColor}
                      fontSize={{ base: '16px', md: '13px' }}
                      fontWeight={750}
                      _hover={{ borderColor }}
                      _focusVisible={{
                        borderColor: inputFocusBorder,
                        boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                      }}
                      sx={{
                        '> option': {
                          color: documentText,
                          backgroundColor: panelBg,
                        },
                      }}
                    >
                      {qmeLetterTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Perspective
                    </Text>
                    <Flex
                      role="group"
                      aria-label="QME/AME perspective"
                      bg={qmeControlBg}
                      border="1px solid"
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      p={1}
                      gap={1}
                      minW={0}
                    >
                      {qmePerspectiveOptions.map(option => {
                        const isActive = qmePerspective === option.value;
                        return (
                          <Button
                            key={option.value}
                            flex={1}
                            minW={0}
                            h={{ base: '34px', md: '30px' }}
                            size="xs"
                            borderRadius="6px"
                            bg={isActive ? qmeControlActiveBg : 'transparent'}
                            color={isActive ? headingColor : mutedText}
                            fontSize="12px"
                            fontWeight={isActive ? 750 : 600}
                            whiteSpace="nowrap"
                            _hover={{ bg: isActive ? qmeControlActiveBg : actionHover }}
                            _focusVisible={{
                              boxShadow: `0 0 0 2px ${inputFocusBorder}`,
                            }}
                            onClick={() => setQmePerspective(option.value)}
                          >
                            {option.label}
                          </Button>
                        );
                      })}
                    </Flex>
                  </Box>

                  <Box minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Tone
                    </Text>
                    <Select
                      aria-label="QME/AME tone"
                      name="qme-tone"
                      value={qmeTone}
                      onChange={event => setQmeTone(event.target.value as QmeTone)}
                      h={{ base: '40px', md: '34px' }}
                      bg={qmeControlBg}
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      color={headingColor}
                      fontSize={{ base: '16px', md: '13px' }}
                      fontWeight={750}
                      _hover={{ borderColor }}
                      _focusVisible={{
                        borderColor: inputFocusBorder,
                        boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                      }}
                      sx={{
                        '> option': {
                          color: documentText,
                          backgroundColor: panelBg,
                        },
                      }}
                    >
                      {qmeToneOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  </Box>
                </Box>

                <Box
                  borderTop="1px solid"
                  borderColor={qmeSectionBorder}
                  bg={qmeSectionBg}
                  borderRadius="0"
                  pt={3}
                >
                  <Text fontSize="11px" fontWeight={750} color={headingColor} mb={2}>
                    Claim and packet
                  </Text>
                  <Box
                    display="grid"
                    gridTemplateColumns={{ base: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }}
                    gap={3}
                  >
                  <Box minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Claim status
                    </Text>
                    <Flex
                      role="group"
                      aria-label="QME/AME claim status"
                      bg={qmeControlBg}
                      border="1px solid"
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      p={1}
                      gap={1}
                      minW={0}
                    >
                      {qmeClaimStatusOptions.map(option => {
                        const isActive = qmeClaimStatus === option.value;
                        return (
                          <Button
                            key={option.value}
                            flex={1}
                            minW={0}
                            h={{ base: '34px', md: '30px' }}
                            size="xs"
                            borderRadius="6px"
                            bg={isActive ? qmeControlActiveBg : 'transparent'}
                            color={isActive ? headingColor : mutedText}
                            fontSize="12px"
                            fontWeight={isActive ? 750 : 600}
                            whiteSpace="normal"
                            _hover={{ bg: isActive ? qmeControlActiveBg : actionHover }}
                            _focusVisible={{ boxShadow: `0 0 0 2px ${inputFocusBorder}` }}
                            onClick={() => setQmeClaimStatus(option.value)}
                          >
                            {option.label}
                          </Button>
                        );
                      })}
                    </Flex>
                  </Box>

                  <Box minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Records packet
                    </Text>
                    <Flex
                      role="group"
                      aria-label="QME/AME records packet status"
                      bg={qmeControlBg}
                      border="1px solid"
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      p={1}
                      gap={1}
                      minW={0}
                    >
                      {qmePacketStatusOptions.map(option => {
                        const isActive = qmePacketStatus === option.value;
                        return (
                          <Button
                            key={option.value}
                            flex={1}
                            minW={0}
                            h={{ base: '34px', md: '30px' }}
                            size="xs"
                            borderRadius="6px"
                            bg={isActive ? qmeControlActiveBg : 'transparent'}
                            color={isActive ? headingColor : mutedText}
                            fontSize="12px"
                            fontWeight={isActive ? 750 : 600}
                            whiteSpace="normal"
                            _hover={{ bg: isActive ? qmeControlActiveBg : actionHover }}
                            _focusVisible={{ boxShadow: `0 0 0 2px ${inputFocusBorder}` }}
                            onClick={() => setQmePacketStatus(option.value)}
                          >
                            {option.label}
                          </Button>
                        );
                      })}
                    </Flex>
                  </Box>
                  </Box>
                </Box>

                <Box
                  borderTop="1px solid"
                  borderColor={qmeSectionBorder}
                  bg={qmeSectionBg}
                  pt={3}
                >
                  <Text fontSize="11px" fontWeight={750} color={headingColor} mb={2}>
                    Case and exam header
                  </Text>
                  <Box
                    display="grid"
                    gridTemplateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }}
                    gap={3}
                  >
                    {[
                      { label: 'Evaluator', value: qmeEvaluator, setter: setQmeEvaluator, placeholder: 'Dr. [QME NAME]' },
                      { label: 'Specialty', value: qmeSpecialty, setter: setQmeSpecialty, placeholder: 'Orthopedic / psych…' },
                      { label: 'Exam date', value: qmeExamDate, setter: setQmeExamDate, placeholder: 'MM/DD/YYYY' },
                      { label: 'Applicant', value: qmeApplicant, setter: setQmeApplicant, placeholder: 'Applicant name' },
                      { label: 'Defendant', value: qmeDefendant, setter: setQmeDefendant, placeholder: 'Employer / carrier' },
                      { label: 'Claim #', value: qmeClaimNumber, setter: setQmeClaimNumber, placeholder: 'Claim number' },
                      { label: 'DOI', value: qmeDateOfInjury, setter: setQmeDateOfInjury, placeholder: 'Date of injury' },
                    ].map(field => (
                      <Box key={field.label} minW={0}>
                        <Text
                          as="label"
                          display="block"
                          fontSize="10px"
                          fontWeight={750}
                          color={mutedText}
                          mb={1}
                          textTransform="uppercase"
                          letterSpacing="0"
                        >
                          {field.label}
                        </Text>
                        <Input
                          value={field.value}
                          onChange={event => field.setter(event.target.value)}
                          name={`qme-${field.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          size="sm"
                          h={{ base: '40px', md: '34px' }}
                          fontSize={{ base: '16px', md: '13px' }}
                          bg={qmeTextareaBg}
                          borderColor={qmeControlBorder}
                          borderRadius="8px"
                          placeholder={field.placeholder}
                          _placeholder={{ color: mutedText, opacity: 0.7 }}
                          _hover={{ borderColor }}
                          _focusVisible={{
                            borderColor: inputFocusBorder,
                            boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box
                  borderTop="1px solid"
                  borderColor={qmeSectionBorder}
                  bg={qmeSectionBg}
                  pt={3}
                >
                  <Flex align="center" justify="space-between" gap={3} mb={3}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor}>
                      Issues
                    </Text>
                    <Text fontSize="11px" color={mutedText} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {qmeSelectedIssues.length}/{qmeIssueOptions.length}
                    </Text>
                  </Flex>
                  <Box
                    display="grid"
                    gridTemplateColumns={{ base: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }}
                    gap={2}
                  >
                    {qmeIssueOptions.map(issue => {
                      const isActive = qmeSelectedIssues.includes(issue.label);
                      return (
                        <Button
                          key={issue.label}
                          size="xs"
                          h={{ base: '36px', md: '32px' }}
                          px={3}
                          w="100%"
                          borderRadius="7px"
                          border="1px solid"
                          borderColor={isActive ? inputFocusBorder : qmeControlBorder}
                          bg={isActive ? qmeControlActiveBg : qmeControlBg}
                          color={isActive ? headingColor : mutedText}
                          fontSize="12px"
                          fontWeight={isActive ? 750 : 600}
                          whiteSpace="normal"
                          leftIcon={isActive ? <FaCheckCircle size={10} /> : undefined}
                          justifyContent="flex-start"
                          _hover={{ bg: isActive ? qmeControlActiveBg : actionHover, color: headingColor }}
                          _focusVisible={{
                            boxShadow: `0 0 0 2px ${inputFocusBorder}`,
                          }}
                          onClick={() => toggleQmeIssue(issue.label)}
                        >
                          {issue.label}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {selectedQmeIssues.length > 0 && (
                  <Box
                    borderTop="1px solid"
                    borderColor={qmeSectionBorder}
                    bg={qmeSectionBg}
                    pt={3}
                  >
                    <Flex align="center" justify="space-between" gap={3} mb={3}>
                      <Text fontSize="11px" fontWeight={750} color={headingColor}>
                        Question groups
                      </Text>
                      <Text fontSize="11px" color={mutedText}>
                        Drafted into evaluator questions
                      </Text>
                    </Flex>
                    <Box
                      display="grid"
                      gridTemplateColumns={{ base: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }}
                      gap={2}
                    >
                      {selectedQmeIssues.map(issue => (
                        <Box
                          key={issue.label}
                          border="1px solid"
                          borderColor={qmeControlBorder}
                          borderRadius="8px"
                          bg={qmeControlBg}
                          p={3}
                        >
                          <Flex align="center" justify="space-between" gap={2} mb={2}>
                            <Text fontSize="12px" fontWeight={750} color={headingColor} noOfLines={1}>
                              {issue.label}
                            </Text>
                            <Box
                              as="span"
                              px={2}
                              py="2px"
                              borderRadius="999px"
                              bg={qmeControlActiveBg}
                              color={headingColor}
                              fontSize="10px"
                              fontWeight={750}
                              lineHeight="1.25"
                              flexShrink={0}
                            >
                              MED-LEGAL
                            </Box>
                          </Flex>
                          <Flex direction="column" gap={1.5}>
                            {issue.questions.map(question => (
                              <Flex key={question} align="flex-start" gap={2}>
                                <Box mt="7px" w="4px" h="4px" borderRadius="999px" bg={headingColor} flexShrink={0} />
                                <Text fontSize="12px" color={documentText} lineHeight="1.45">
                                  {question}
                                </Text>
                              </Flex>
                            ))}
                          </Flex>
                          <Text mt={2} fontSize="11px" color={mutedText} lineHeight="1.4">
                            {issue.risk}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                <Box
                  borderTop="1px solid"
                  borderColor={qmeSectionBorder}
                  bg={qmeSectionBg}
                  pt={3}
                >
                  <Text fontSize="11px" fontWeight={750} color={headingColor} mb={2}>
                    Facts and packet details
                  </Text>
                <Flex gap={3} direction={{ base: 'column', lg: 'row' }}>
                  <Box flex={1} minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Undisputed facts
                    </Text>
                    <Textarea
                      aria-label="QME/AME undisputed facts"
                      name="qme-undisputed-facts"
                      value={qmeUndisputedFacts}
                      onChange={event => setQmeUndisputedFacts(event.target.value)}
                      minH="96px"
                      resize="vertical"
                      fontSize={{ base: '16px', md: '13px' }}
                      lineHeight="1.55"
                      bg={qmeTextareaBg}
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      placeholder="Facts both sides can live with: DOI, job, accepted body parts, exam purpose…"
                      _placeholder={{ color: mutedText, opacity: 0.72 }}
                      _hover={{ borderColor }}
                      _focusVisible={{
                        borderColor: inputFocusBorder,
                        boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                      }}
                    />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Disputed facts
                    </Text>
                    <Textarea
                      aria-label="QME/AME disputed facts"
                      name="qme-disputed-facts"
                      value={qmeDisputedFacts}
                      onChange={event => setQmeDisputedFacts(event.target.value)}
                      minH="96px"
                      resize="vertical"
                      fontSize={{ base: '16px', md: '13px' }}
                      lineHeight="1.55"
                      bg={qmeTextareaBg}
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      placeholder="Facts that must be framed as party positions, not established truth…"
                      _placeholder={{ color: mutedText, opacity: 0.72 }}
                      _hover={{ borderColor }}
                      _focusVisible={{
                        borderColor: inputFocusBorder,
                        boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                      }}
                    />
                  </Box>
                </Flex>

                <Flex gap={3} direction={{ base: 'column', lg: 'row' }}>
                  <Box flex={1} minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Applicant position
                    </Text>
                    <Textarea
                      aria-label="QME/AME applicant position"
                      name="qme-applicant-position"
                      value={qmeApplicantPosition}
                      onChange={event => setQmeApplicantPosition(event.target.value)}
                      minH="82px"
                      resize="vertical"
                      fontSize={{ base: '16px', md: '13px' }}
                      lineHeight="1.55"
                      bg={qmeTextareaBg}
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      placeholder="Applicant-side framing or disputed medical facts…"
                      _placeholder={{ color: mutedText, opacity: 0.72 }}
                      _hover={{ borderColor }}
                      _focusVisible={{
                        borderColor: inputFocusBorder,
                        boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                      }}
                    />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Defense position
                    </Text>
                    <Textarea
                      aria-label="QME/AME defense position"
                      name="qme-defense-position"
                      value={qmeDefensePosition}
                      onChange={event => setQmeDefensePosition(event.target.value)}
                      minH="82px"
                      resize="vertical"
                      fontSize={{ base: '16px', md: '13px' }}
                      lineHeight="1.55"
                      bg={qmeTextareaBg}
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      placeholder="Defense-side framing, denial basis, or disputed medical facts…"
                      _placeholder={{ color: mutedText, opacity: 0.72 }}
                      _hover={{ borderColor }}
                      _focusVisible={{
                        borderColor: inputFocusBorder,
                        boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                      }}
                    />
                  </Box>
                </Flex>

                <Flex gap={3} direction={{ base: 'column', lg: 'row' }}>
                  <Box flex={1} minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Records packet
                    </Text>
                    <Textarea
                      aria-label="QME/AME records"
                      name="qme-records"
                      value={qmeRecords}
                      onChange={event => setQmeRecords(event.target.value)}
                      minH="96px"
                      resize="vertical"
                      fontSize={{ base: '16px', md: '13px' }}
                      lineHeight="1.55"
                      bg={qmeTextareaBg}
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      placeholder="Medical reports, imaging, benefit notices, job descriptions, deposition excerpts…"
                      _placeholder={{ color: mutedText, opacity: 0.72 }}
                      _hover={{ borderColor }}
                      _focusVisible={{
                        borderColor: inputFocusBorder,
                        boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                      }}
                    />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor} mb={1.5}>
                      Do not include
                    </Text>
                    <Textarea
                      aria-label="QME/AME excluded facts"
                      name="qme-excluded-facts"
                      value={qmeExcludedFacts}
                      onChange={event => setQmeExcludedFacts(event.target.value)}
                      minH="96px"
                      resize="vertical"
                      fontSize={{ base: '16px', md: '13px' }}
                      lineHeight="1.55"
                      bg={qmeTextareaBg}
                      borderColor={qmeControlBorder}
                      borderRadius="8px"
                      placeholder="Settlement, privilege, unsupported facts, legal defenses not medically relevant…"
                      _placeholder={{ color: mutedText, opacity: 0.72 }}
                      _hover={{ borderColor }}
                      _focusVisible={{
                        borderColor: inputFocusBorder,
                        boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                      }}
                    />
                  </Box>
                </Flex>
                </Box>

                <Box
                  borderTop="1px solid"
                  borderColor={qmeSectionBorder}
                  bg={qmeSectionBg}
                  pt={3}
                >
                  <Text fontSize="11px" fontWeight={750} color={headingColor} mb={2}>
                    Custom questions
                  </Text>
                  <Textarea
                    aria-label="QME/AME custom questions"
                    name="qme-custom-questions"
                    value={qmeCustomQuestions}
                    onChange={event => setQmeCustomQuestions(event.target.value)}
                    minH="74px"
                    resize="vertical"
                    fontSize={{ base: '16px', md: '13px' }}
                    lineHeight="1.55"
                    bg={qmeTextareaBg}
                    borderColor={qmeControlBorder}
                    borderRadius="8px"
                    placeholder="Add required evaluator questions or constraints…"
                    _placeholder={{ color: mutedText, opacity: 0.72 }}
                    _hover={{ borderColor }}
                    _focusVisible={{
                      borderColor: inputFocusBorder,
                      boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                    }}
                  />
                </Box>

                <Box
                  borderTop="1px solid"
                  borderColor={qmeSectionBorder}
                  bg={qmeSectionBg}
                  pt={3}
                >
                  <Flex align="center" justify="space-between" gap={3} mb={3}>
                    <Text fontSize="11px" fontWeight={750} color={headingColor}>
                      Advocacy risk check
                    </Text>
                    <Text fontSize="11px" color={mutedText}>
                      Before sending to evaluator
                    </Text>
                  </Flex>
                  <Flex gap={2} wrap="wrap">
                    {qmeRiskItems.map(item => (
                      <Flex
                        key={item.label}
                        align="center"
                        gap={2}
                        minW={{ base: '100%', md: 'calc(50% - 4px)' }}
                        flex="1 1 220px"
                        border="1px solid"
                        borderColor={item.done ? qmeControlBorder : missingFieldRing}
                        bg={item.done ? qmeControlBg : missingFieldBg}
                        borderRadius="8px"
                        px={3}
                        py={2}
                      >
                        <Flex
                          align="center"
                          justify="center"
                          w="20px"
                          h="20px"
                          borderRadius="999px"
                          bg={item.done ? qmeControlActiveBg : missingFieldHoverBg}
                          color={item.done ? headingColor : typePillColor}
                          flexShrink={0}
                          fontSize="10px"
                        >
                          {item.done ? <FaCheckCircle aria-hidden /> : <FaSearch aria-hidden />}
                        </Flex>
                        <Box minW={0}>
                          <Text fontSize="12px" fontWeight={750} color={headingColor} noOfLines={1}>
                            {item.label}
                          </Text>
                          <Text fontSize="11px" color={mutedText} noOfLines={1}>
                            {item.detail}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                  </Flex>
                </Box>

                <Flex
                  align={{ base: 'stretch', md: 'center' }}
                  justify="space-between"
                  gap={2}
                  pt={0}
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Button
                    size="sm"
                    h={{ base: '40px', md: '34px' }}
                    variant="ghost"
                    color={headingColor}
                    fontSize="13px"
                    fontWeight={700}
                    leftIcon={isReviewingQmeDraft ? <Spinner size="xs" /> : <FaSearch size={12} />}
                    isDisabled={isReviewingQmeDraft || isApplyingQmeBuilder}
                    _hover={{ bg: actionHover }}
                    _focusVisible={{
                      boxShadow: `0 0 0 2px ${inputFocusBorder}`,
                    }}
                    onClick={reviewQmeAdvocacy}
                  >
                    {isReviewingQmeDraft ? 'Reviewing' : 'Review Advocacy'}
                  </Button>
                  <Button
                    size="sm"
                    h={{ base: '40px', md: '34px' }}
                    bg={editSegmentBg}
                    color={editSegmentColor}
                    border="1px solid"
                    borderColor={editSegmentBorder}
                    borderRadius="8px"
                    fontSize="13px"
                    fontWeight={750}
                    leftIcon={isApplyingQmeBuilder ? <Spinner size="xs" color={editSegmentColor} /> : <FaMagic size={12} />}
                    isDisabled={isApplyingQmeBuilder || isReviewingQmeDraft}
                    backdropFilter="blur(14px) saturate(135%)"
                    boxShadow={editSegmentShadow}
                    _hover={{ bg: editSegmentHoverBg, boxShadow: editSegmentShadow }}
                    _focusVisible={{
                      boxShadow: `${editSegmentShadow}, 0 0 0 3px ${editSegmentFocusRing}`,
                    }}
                    _disabled={{
                      opacity: 0.58,
                      cursor: 'not-allowed',
                      boxShadow: 'none',
                    }}
                    onClick={applyQmeBuilderToDraft}
                  >
                    {isApplyingQmeBuilder ? 'Applying' : qmePrimaryActionLabel}
                  </Button>
                </Flex>
              </Flex>
            </Box>
          )}
        </Box>
      )}

      {/* ── Content area ── */}
      {!hasUsableDraft ? (
        <Flex
          flex={1}
          minH={0}
          align="center"
          justify="center"
          px={{ base: 6, md: 10, lg: 12 }}
          py={{ base: 6, md: 8 }}
          color={documentText}
        >
          <Box maxW="520px" textAlign="center">
            <Flex
              mx="auto"
              mb={4}
              align="center"
              justify="center"
              w="42px"
              h="42px"
              borderRadius="8px"
              bg={qmeControlActiveBg}
              color={headingColor}
            >
              <FaMagic size={16} aria-hidden />
            </Flex>
            <Text fontSize="16px" fontWeight={750} color={headingColor} mb={2}>
              Build the QME/AME letter
            </Text>
            <Text fontSize="13px" color={mutedText} lineHeight="1.65">
              Fill the evaluator, records, fact, and issue inputs above, then generate the attorney-ready letter from that structure.
            </Text>
          </Box>
        </Flex>
      ) : (
        <Box
          ref={documentRef}
          flex={1}
          minH={0}
          overflowY="auto"
          color={documentText}
          px={{ base: 6, md: 10, lg: 12 }}
          py={{ base: 6, md: 8 }}
          fontFamily="Bitter, serif"
          fontSize="15.5px"
          lineHeight="1.85"
          wordBreak="break-word"
          userSelect="text"
          position="relative"
          sx={{
            WebkitUserSelect: 'text',
            textRendering: 'optimizeLegibility',
            maxWidth: '100%',
            '::selection': {
              bg: 'rgba(255, 193, 14, 0.34)',
            },
            'mark[data-legal-draft-field-highlight="true"]': {
              display: 'inline',
              px: '2px',
              py: 0,
              bg: missingFieldHoverBg,
              color: documentText,
              borderBottom: '1.5px solid',
              borderColor: headingColor,
              borderRadius: '2px',
              boxShadow: `0 0 0 3px ${missingFieldBg}`,
              fontStyle: 'italic',
              fontWeight: 600,
              lineHeight: 'inherit',
              verticalAlign: 'baseline',
              scrollMarginTop: '120px',
            },
            'mark[data-legal-draft-missing-field="true"]': {
              display: 'inline',
              px: '2px',
              py: 0,
              bg: 'transparent',
              color: documentText,
              borderBottom: '1px dotted',
              borderColor: missingFieldRing,
              borderRadius: '2px',
              cursor: 'pointer',
              fontStyle: 'italic',
              fontWeight: 500,
              lineHeight: 'inherit',
              verticalAlign: 'baseline',
              transition: 'background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease',
            },
            'mark[data-legal-draft-missing-field="true"]:hover': {
              bg: missingFieldHoverBg,
              borderColor: headingColor,
              boxShadow: `0 2px 0 ${missingFieldBg}`,
            },
            'ins[data-legal-draft-change="add"]': {
              bg: changeAddBg,
              borderBottom: '1px solid',
              borderColor: changeAddBorder,
              borderRadius: '2px',
              color: 'inherit',
              px: '1px',
              textDecoration: 'none',
              boxDecorationBreak: 'clone',
              WebkitBoxDecorationBreak: 'clone',
              transition: 'background-color 180ms ease, border-color 180ms ease',
            },
            'del[data-legal-draft-change="remove"]': {
              bg: changeRemoveBg,
              borderBottom: '1px solid',
              borderColor: changeRemoveBorder,
              borderRadius: '2px',
              color: mutedText,
              px: '1px',
              textDecoration: 'line-through',
              textDecorationThickness: '1px',
              boxDecorationBreak: 'clone',
              WebkitBoxDecorationBreak: 'clone',
              transition: 'background-color 180ms ease, border-color 180ms ease',
            },
          }}
          onClick={handleDraftClick}
          onMouseUp={handleDraftMouseUp}
          onMouseDown={beginSelection}
          onDoubleClick={handleDraftDoubleClick}
          onTouchEnd={() => scheduleSelectionCapture()}
          onKeyUp={() => scheduleSelectionCapture()}
        >
          {draftSelection?.rects.map((rect, index) => (
            <Box
              key={`${rect.top}-${rect.left}-${index}`}
              aria-hidden
              position="absolute"
              top={`${rect.top}px`}
              left={`${rect.left}px`}
              w={`${rect.width}px`}
              h={`${rect.height}px`}
              bg={selectedTextBg}
              borderRadius="3px"
              boxShadow={`0 0 0 1px ${selectedTextRing}`}
              pointerEvents="none"
              zIndex={1}
              transformOrigin="center"
              animation={(isApplyingObservation || isEditSettling) && !prefersReducedMotion ? `${selectionEditPulse} 900ms ease-in-out infinite` : undefined}
            />
          ))}
          {(isApplyingObservation || isEditSettling) && draftSelection?.rects[0] && (
            <Flex
              aria-hidden
              position="absolute"
              top={`${Math.max(0, draftSelection.rects[0].top - 30)}px`}
              left={`${draftSelection.rects[0].left}px`}
              align="center"
              gap={1.5}
              h="24px"
              px={2}
              borderRadius="999px"
              bg={popoverBg}
              border="1px solid"
              borderColor={borderColor}
              boxShadow={popoverShadow}
              color={headingColor}
              fontSize="11px"
              fontWeight={700}
              zIndex={2}
              pointerEvents="none"
            >
              {isApplyingObservation && <Spinner size="xs" color={headingColor} />}
              {isApplyingObservation ? 'Editing' : 'Updated'}
            </Flex>
          )}
          <Markdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h1: ({ node, ...props }) => <Box as="h1" fontSize="22px" fontWeight={700} color={headingColor} mb={4} mt={1} lineHeight="1.3" letterSpacing="-0.01em" {...props} />,
              h2: ({ node, ...props }) => <Box as="h2" fontSize="18px" fontWeight={700} color={headingColor} mb={3} mt={7} lineHeight="1.35" {...props} />,
              h3: ({ node, ...props }) => <Box as="h3" fontSize="16px" fontWeight={700} color={headingColor} mb={2} mt={5} lineHeight="1.4" {...props} />,
              p: ({ node, ...props }) => <Box as="p" mb={4} {...props} />,
              ul: ({ node, ...props }) => <Box as="ul" pl={6} mb={4} {...props} />,
              ol: ({ node, ...props }) => <Box as="ol" pl={6} mb={4} {...props} />,
              li: ({ node, ...props }) => <Box as="li" mb={2} pl={1} {...props} />,
              hr: ({ node, ...props }) => <Box as="hr" border={0} borderTop="1px solid" borderColor={documentMutedRule} my={7} {...props} />,
              a: ({ node, href, ...props }) => {
                const linkText = React.Children.toArray((props as any).children).join('').replace(/^\[+|\]+$/g, '');
                const isChatSocReference = !isLegalCodeUrl(href)
                  && (
                    isSullivanOnCompUrl(href)
                    || /^SOC\b/i.test(linkText)
                    || /^ChatSOC\b/i.test(linkText)
                  );

                if (!showChatSocReferences && isChatSocReference) {
                  return null;
                }

                return (
                  <Box
                    as="a"
                    href={href}
                    color={headingColor}
                    fontWeight={600}
                    textDecoration="underline"
                    textDecorationColor={documentMarginRule}
                    textUnderlineOffset="3px"
                    _hover={{ color: typePillColor, textDecorationColor: headingColor }}
                    {...props}
                  />
                );
              },
              blockquote: ({ node, ...props }) => (
                <Box
                  as="blockquote"
                  borderLeft="3px solid"
                  borderColor={borderColor}
                  bg={quoteBg}
                  pl={4}
                  py={2}
                  my={5}
                  color={mutedText}
                  borderRadius="0 6px 6px 0"
                  {...props}
                />
              ),
            } as Components}
          >
            {displayPreviewText}
          </Markdown>
          {isVersionCompareActive && !versionComparisonText && (
            <Box
              mt={4}
              px={3}
              py={2.5}
              border="1px solid"
              borderColor={chipBorder}
              borderRadius="8px"
              bg={fieldCardBg}
              fontFamily='"Elza", sans-serif'
            >
              <Text fontSize="12px" fontWeight={750} color={headingColor}>
                No textual differences found between these versions.
              </Text>
            </Box>
          )}
        </Box>
      )}

      {/* ── Version footer ── */}
      <Flex
          minH="46px"
          borderTop="1px solid"
          borderColor={borderColor}
          align="center"
          justify="space-between"
          gap={3}
          px={4}
          py={2}
          flexShrink={0}
          bg={panelBg}
        >
          <Flex align="center" gap={2} minW={0}>
            <Flex
              w="26px"
              h="26px"
              borderRadius="7px"
              align="center"
              justify="center"
              bg={toggleBg}
              color={headingColor}
              flexShrink={0}
            >
              {isVersionHistoryLoading ? <Spinner size="xs" color={headingColor} /> : <FaHistory size={12} />}
            </Flex>
            <Box minW={0}>
              <Text fontSize="12px" fontWeight={700} color={headingColor} noOfLines={1}>
                Version history
              </Text>
              <Text fontSize="11px" color={mutedText} noOfLines={1} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {isVersionHistoryLoading ? 'Loading saved versions…' : versionSummary}
              </Text>
            </Box>
          </Flex>

          <Flex align="center" gap={2} flexShrink={0} wrap="wrap" justify="flex-end">
            {isVersionHistoryLoading ? (
              <>
                <Button
                  size="sm"
                  h="30px"
                  px={3}
                  borderRadius="7px"
                  fontSize="12px"
                  fontWeight={700}
                  leftIcon={<FaExchangeAlt size={11} aria-hidden />}
                  variant="ghost"
                  color={mutedText}
                  border="1px solid"
                  borderColor={chipBorder}
                  isDisabled
                >
                  Changes
                </Button>
                <IconButton
                  aria-label="Previous version loading"
                  icon={<FaChevronLeft />}
                  size="sm"
                  h="30px"
                  minW="30px"
                  variant="ghost"
                  color={mutedText}
                  borderRadius="7px"
                  isDisabled
                />
                <IconButton
                  aria-label="Next version loading"
                  icon={<FaChevronRight />}
                  size="sm"
                  h="30px"
                  minW="30px"
                  variant="ghost"
                  color={mutedText}
                  borderRadius="7px"
                  isDisabled
                />
              </>
            ) : (
              <>
                {canCompareVersion && (
                  <Button
                    size="sm"
                    h="30px"
                    px={3}
                    borderRadius="7px"
                    fontSize="12px"
                    fontWeight={700}
                    leftIcon={<FaExchangeAlt size={11} aria-hidden />}
                    aria-pressed={isComparingVersions}
                    variant={isComparingVersions ? 'solid' : 'ghost'}
                    bg={isComparingVersions ? editSegmentBg : 'transparent'}
                    color={isComparingVersions ? editSegmentColor : headingColor}
                    border="1px solid"
                    borderColor={isComparingVersions ? editSegmentBorder : chipBorder}
                    _hover={{
                      bg: isComparingVersions ? editSegmentHoverBg : actionHover,
                    }}
                    _focusVisible={{
                      boxShadow: `0 0 0 2px ${inputFocusBorder}`,
                    }}
                    onClick={() => {
                      setChangePreviewText(null);
                      setIsComparingVersions(current => !current);
                    }}
                  >
                    {isComparingVersions ? 'Hide changes' : 'Changes'}
                  </Button>
                )}
                {isComparingVersions && canCompareVersion && (
                  <Flex
                    align="center"
                    gap={1.5}
                    color={mutedText}
                    fontSize="11px"
                    fontWeight={700}
                    flexShrink={0}
                    display={{ base: 'none', md: 'flex' }}
                  >
                    <Box as="span" px={2} py="2px" borderRadius="6px" bg={changeAddBg} border="1px solid" borderColor={changeAddBorder}>
                      Added
                    </Box>
                    <Box as="span" px={2} py="2px" borderRadius="6px" bg={changeRemoveBg} border="1px solid" borderColor={changeRemoveBorder}>
                      Removed
                    </Box>
                  </Flex>
                )}
                <Tooltip label="Previous version" placement="top">
                  <IconButton
                    aria-label="Previous version"
                    icon={<FaChevronLeft />}
                    size="sm"
                    h="30px"
                    minW="30px"
                    variant="ghost"
                    color={mutedText}
                    borderRadius="7px"
                    isDisabled={currentVersionIndex === 0}
                    onClick={() => handleVersionChange(currentVersionIndex - 1)}
                  />
                </Tooltip>
                <Tooltip label="Next version" placement="top">
                  <IconButton
                    aria-label="Next version"
                    icon={<FaChevronRight />}
                    size="sm"
                    h="30px"
                    minW="30px"
                    variant="ghost"
                    color={mutedText}
                    borderRadius="7px"
                    isDisabled={isViewingLatestVersion}
                    onClick={() => handleVersionChange(currentVersionIndex + 1)}
                  />
                </Tooltip>
                {!isViewingLatestVersion && (
                  <Button
                    size="sm"
                    h="30px"
                    px={3}
                    borderRadius="7px"
                    fontSize="12px"
                    fontWeight={700}
                    bg={headingColor}
                    color="white"
                    _hover={{ bg: headingColor, opacity: 0.92 }}
                    onClick={() => handleVersionChange(latestVersionIndex)}
                  >
                    Latest
                  </Button>
                )}
              </>
            )}
          </Flex>
      </Flex>

      {/* ── Selection popover ── */}
      {draftSelection && (
        <Portal>
          <Box
            position="fixed"
            left={`${draftSelection.x}px`}
            top={`${selectionPopoverLayout?.top ?? draftSelection.y}px`}
            transform={selectionPopoverLayout?.transform}
            transformOrigin={selectionPopoverLayout?.transformOrigin}
            zIndex={3000}
            bg={isNoteOpen ? popoverBg : 'transparent'}
            border={isNoteOpen ? '1px solid' : '0'}
            borderColor={isNoteOpen ? borderColor : 'transparent'}
            boxShadow={isNoteOpen ? popoverShadow : 'none'}
            borderRadius={isNoteOpen ? '10px' : 0}
            p={isNoteOpen ? 2 : 0}
            w={isNoteOpen ? { base: 'calc(100vw - 32px)', md: '360px' } : 'auto'}
            maxW="calc(100vw - 32px)"
            maxH={selectionPopoverLayout?.maxH}
            overflowY={isNoteOpen ? 'auto' : 'visible'}
            overscrollBehavior={isNoteOpen ? 'contain' : undefined}
          >
            {!isNoteOpen ? (
              <Button
                aria-label="Work on this selection"
                leftIcon={<FaMagic />}
                size="sm"
                bg={editSegmentBg}
                color={editSegmentColor}
                border="1px solid"
                borderColor={editSegmentBorder}
                borderRadius="8px"
                minW="auto"
                h="36px"
                px={3.5}
                fontSize="13px"
                fontWeight={700}
                backdropFilter="blur(14px) saturate(135%)"
                boxShadow={editSegmentShadow}
                _hover={{ bg: editSegmentHoverBg, transform: 'translateY(-1px)', boxShadow: editSegmentShadow }}
                _active={{ transform: 'translateY(0)' }}
                _focusVisible={{
                  boxShadow: `${editSegmentShadow}, 0 0 0 3px ${editSegmentFocusRing}`,
                }}
                onClick={() => setIsNoteOpen(true)}
              >
                Work on This
              </Button>
            ) : (
              <Box>
                <Textarea
                  ref={noteInputRef}
                  value={observation}
                  onChange={event => setObservation(event.target.value)}
                  onKeyDown={event => {
                    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                      event.preventDefault();
                      submitObservation();
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      cancelObservation();
                    }
                  }}
                  placeholder="What should change, or ask a question…"
                  minH="92px"
                  maxH="180px"
                  resize="vertical"
                  fontSize={{ base: '16px', md: '13px' }}
                  lineHeight="1.55"
                  bg="transparent"
                  border="0"
                  borderRadius="6px"
                  p={2}
                  _placeholder={{ color: mutedText, opacity: 0.82 }}
                  _focusVisible={{
                    boxShadow: 'none',
                  }}
                />
                <Flex
                  justify="space-between"
                  align="center"
                  gap={2}
                  mt={1.5}
                  pt={2}
                  borderTop="1px solid"
                  borderColor={borderColor}
                >
                  <Text fontSize="11px" color={mutedText}>
                    Press Esc to close
                  </Text>
                  <Flex align="center" gap={2}>
                    <Button size="sm" variant="ghost" h="30px" fontSize="13px" onClick={cancelObservation} isDisabled={isApplyingObservation}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      h="30px"
                      bg={editSegmentBg}
                      color={editSegmentColor}
                      border="1px solid"
                      borderColor={editSegmentBorder}
                      borderRadius="7px"
                      fontSize="13px"
                      fontWeight={700}
                      leftIcon={isApplyingObservation ? <Spinner size="xs" color={editSegmentColor} /> : undefined}
                      isDisabled={!observation.trim() || isApplyingObservation}
                      backdropFilter="blur(14px) saturate(135%)"
                      boxShadow={editSegmentShadow}
                      _hover={{ bg: editSegmentHoverBg, boxShadow: editSegmentShadow }}
                      _focusVisible={{
                        boxShadow: `${editSegmentShadow}, 0 0 0 3px ${editSegmentFocusRing}`,
                      }}
                      _disabled={{
                        opacity: 0.52,
                        cursor: 'not-allowed',
                        boxShadow: 'none',
                      }}
                      onClick={submitObservation}
                    >
                      {isApplyingObservation ? 'Editing' : 'Apply'}
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            )}
          </Box>
        </Portal>
      )}
    </Flex>
  );
};

export default LegalDraftPanel;
